// src/pages/ModeratorFormBuilder.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  IconButton,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Radio,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Select,
  Switch,
  InputLabel,
  FormControl,
  Chip,
  Stack,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Forum as ForumIcon,
  Clear as ClearIcon,
  Publish as PublishIcon,
  DoneAll as DoneAllIcon,
  Undo as UndoIcon,
  BarChart as BarChartIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  CheckBox as CheckBoxIcon,
  StarRate as StarRateIcon,
  OndemandVideo as OndemandVideoIcon,
  Image as ImageIcon,
  SmartToy as SmartToyIcon,
  Info as InfoIcon,
  ViewColumn as ViewColumnIcon,
  TextFields as TextFieldsIcon,
  LibraryAdd as LibraryAddIcon,
  Visibility as VisibilityIcon,
  FilterAlt as FilterAltIcon,
  Password as MaskIcon
} from '@mui/icons-material'
import CodeIcon from '@mui/icons-material/Code'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkIcon from '@mui/icons-material/Link'
import { db } from '../firebase'
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  getDocs
} from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'

// i18n
import { useTranslation } from 'react-i18next'

// Quill (metin editörü)
import ReactQuill from 'react-quill'
import Quill from 'quill'
import 'react-quill/dist/quill.snow.css'
import 'quill/dist/quill.core.css'
import { uploadFile } from '../utils/uploadFile'

// react-beautiful-dnd
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

// -------------------------------------------------------------
// Quill setup
function useQuillModules() {
  const quillRef = useRef(null)

  const handleImage = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        const editor = quillRef.current?.getEditor?.()
        if (!editor) return
        const range = editor.getSelection(true)
        editor.insertEmbed(range.index, 'image', url, Quill.sources.USER)
        editor.setSelection(range.index + 1, 0)

        setTimeout(() => {
          const editorContainer = editor.container
          const newlyInsertedImage = editorContainer.querySelector(`img[src="${url}"]`)
          if (newlyInsertedImage && !newlyInsertedImage.style.width) {
            newlyInsertedImage.style.maxWidth = '100%'
            newlyInsertedImage.style.height = 'auto'
          }
        }, 0)
      } catch (err) {
        console.error('Görsel yüklenirken hata:', err)
      }
    }
  }, [])

  const handleVideo = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'video/*')
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        const editor = quillRef.current?.getEditor?.()
        if (!editor) return
        const range = editor.getSelection(true)
        editor.insertEmbed(range.index, 'video', url, Quill.sources.USER)
      } catch (err) {
        console.error('Video yüklenirken hata:', err)
      }
    }
  }, [])

  const handleLink = useCallback(() => {
    const editor = quillRef.current?.getEditor?.()
    if (!editor) return
    const range = editor.getSelection()
    if (range && range.length > 0) {
      // Burada prompt’u da çeviriye bağlayabilirsiniz (isteğe bağlı).
      const href = prompt('Lütfen linki giriniz:')
      if (href) {
        editor.format('link', href)
      }
    } else {
      alert('Önce link vereceğiniz metni seçiniz.')
    }
  }, [])

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image', 'video'],
          ['clean']
        ],
        handlers: {
          link: handleLink,
          image: handleImage,
          video: handleVideo
        }
      }
    }),
    [handleImage, handleVideo, handleLink]
  )

  return { quillRef, modules }
}

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'link',
  'image',
  'video',
  'align',
  'direction'
]

const QUESTION_TYPE_PALETTES = {
  default: {
    inactiveBg: '#f1f5f9',
    activeBg: '#e0f2fe',
    inactiveColor: '#64748b',
    activeColor: '#2563eb',
    border: '#93c5fd',
    activeShadow: 'rgba(147, 197, 253, 0.25)'
  },
  openEnded: {
    inactiveBg: '#eff6ff',
    activeBg: '#dbeafe',
    inactiveColor: '#1d4ed8',
    activeColor: '#1d4ed8',
    border: '#60a5fa',
    activeShadow: 'rgba(96,165,250,0.30)'
  },
  singleChoice: {
    inactiveBg: '#ecfdf3',
    activeBg: '#bbf7d0',
    inactiveColor: '#15803d',
    activeColor: '#166534',
    border: '#4ade80',
    activeShadow: 'rgba(74,222,128,0.28)'
  },
  multipleChoice: {
    inactiveBg: '#fffbeb',
    activeBg: '#fef3c7',
    inactiveColor: '#92400e',
    activeColor: '#b45309',
    border: '#facc15',
    activeShadow: 'rgba(250,204,21,0.28)'
  },
  pictureChoice: {
    inactiveBg: '#fef2f2',
    activeBg: '#fee2e2',
    inactiveColor: '#b91c1c',
    activeColor: '#b91c1c',
    border: '#fca5a5',
    activeShadow: 'rgba(248,113,113,0.28)'
  },
  masked: {
    inactiveBg: '#f5f3ff',
    activeBg: '#ede9fe',
    inactiveColor: '#6d28d9',
    activeColor: '#5b21b6',
    border: '#a855f7',
    activeShadow: 'rgba(168,85,247,0.28)'
  },
  screening: {
    inactiveBg: '#fdf2ff',
    activeBg: '#fae8ff',
    inactiveColor: '#9d174d',
    activeColor: '#9d174d',
    border: '#f472b6',
    activeShadow: 'rgba(244,114,182,0.28)'
  },
  ratingQuestion: {
    inactiveBg: '#fffbeb',
    activeBg: '#fef3c7',
    inactiveColor: '#92400e',
    activeColor: '#b45309',
    border: '#fdba74',
    activeShadow: 'rgba(251,191,36,0.28)'
  },
  videoQuestion: {
    inactiveBg: '#eff6ff',
    activeBg: '#dbeafe',
    inactiveColor: '#1d4ed8',
    activeColor: '#1d4ed8',
    border: '#60a5fa',
    activeShadow: 'rgba(96,165,250,0.30)'
  },
  photoCollage: {
    inactiveBg: '#f3e8ff',
    activeBg: '#e9d5ff',
    inactiveColor: '#6b21a8',
    activeColor: '#581c87',
    border: '#c4b5fd',
    activeShadow: 'rgba(196,181,253,0.28)'
  },
  aiModerator: {
    inactiveBg: '#f0f9ff',
    activeBg: '#e0f2fe',
    inactiveColor: '#0369a1',
    activeColor: '#075985',
    border: '#7dd3fc',
    activeShadow: 'rgba(125,211,252,0.30)'
  },
  infoQuestion: {
    inactiveBg: '#f3f4f6',
    activeBg: '#e5e7eb',
    inactiveColor: '#374151',
    activeColor: '#111827',
    border: '#9ca3af',
    activeShadow: 'rgba(156,163,175,0.28)'
  },
  cardSorting: {
    inactiveBg: '#ecfeff',
    activeBg: '#cffafe',
    inactiveColor: '#0e7490',
    activeColor: '#155e75',
    border: '#22d3ee',
    activeShadow: 'rgba(34,211,238,0.28)'
  }
}

const glassDialogPaperSx = {
  borderRadius: 4,
  overflow: 'hidden',
  backdropFilter: 'blur(14px)',
  background: 'linear-gradient(145deg, rgba(255,255,255,0.92), rgba(236,244,255,0.88))',
  border: '1px solid rgba(255,255,255,0.7)',
  boxShadow: '0 24px 60px rgba(15,23,42,0.28)'
}

const dialogTitleBarSx = {
  px: 3,
  py: 2,
  background: 'linear-gradient(120deg, rgba(148,163,184,0.12), rgba(59,130,246,0.08))',
  borderBottom: '1px solid rgba(148,163,184,0.35)',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backdropFilter: 'blur(10px)'
}

const dialogActionsBarSx = {
  px: 3,
  py: 2,
  position: 'sticky',
  bottom: 0,
  borderTop: '1px solid rgba(148,163,184,0.35)',
  background: 'linear-gradient(180deg, rgba(248,250,252,0.9), rgba(226,232,240,0.9))',
  backdropFilter: 'blur(10px)',
  zIndex: 2
}


const InlineQuillEditor = React.memo(function InlineQuillEditor({ value, onChange }) {
  const { quillRef, modules } = useQuillModules()
  const [anchorEl, setAnchorEl] = useState(null)
  const [clickedImage, setClickedImage] = useState(null)
  const { t } = useTranslation()
  const safeValue = value ?? ''

  useEffect(() => {
    if (!quillRef.current) return
    const editor = quillRef.current?.getEditor?.()
    if (!editor) return
    const root = editor.root

    const handleClick = (e) => {
      if (e.target && e.target.tagName === 'IMG') {
        setClickedImage(e.target)
        setAnchorEl(e.target)
      } else {
        handleCloseMenu()
      }
    }

    root.addEventListener('click', handleClick)
    return () => {
      root.removeEventListener('click', handleClick)
    }
  }, [quillRef])

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setClickedImage(null)
  }

  const handleSelectSize = (size) => {
    if (clickedImage) {
      clickedImage.style.width = size + '%'
    }
    handleCloseMenu()
  }

  return (
    <>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={safeValue}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={t('moderatorFormBuilder.questionTextPlaceholder')}
        style={{ minHeight: '200px' }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => handleSelectSize(25)}>%25</MenuItem>
        <MenuItem onClick={() => handleSelectSize(50)}>%50</MenuItem>
        <MenuItem onClick={() => handleSelectSize(100)}>%100</MenuItem>
      </Menu>
    </>
  )
})



// -------------------------------------------------------------
// ModeratorFormBuilder Bileşeni
export default function ModeratorFormBuilder() {
  const { currentUser, userRole, organization } = useAuth()
  const { t } = useTranslation() // <== i18n Hook
  const navigate = useNavigate()

  // Soru Formları
  const [questionForms, setQuestionForms] = useState([])
  const [loadingQuestionForms, setLoadingQuestionForms] = useState(false)
  const [selectedQuestionForm, setSelectedQuestionForm] = useState(null)

  // Sorular
  const [questions, setQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)

  // Yeni form ismi
  const [newFormName, setNewFormName] = useState('')
  const [addFormDialogOpen, setAddFormDialogOpen] = useState(false)
  const [newFormLanguage, setNewFormLanguage] = useState('tr')

  // Yeni soru
  const [newQuestionText, setNewQuestionText] = useState('')
  const [newQuestionType, setNewQuestionType] = useState('openEnded')
  const [newChoices, setNewChoices] = useState([''])
  const [newAllowOther, setNewAllowOther] = useState(false)
  const [newRequireMedia, setNewRequireMedia] = useState(false)
  const [newRequired, setNewRequired] = useState(false)

  const createEmptyScreeningChoice = () => ({ text: '', screenOut: false, screenOutText: '' })
  const [newScreeningMode, setNewScreeningMode] = useState('single')
  const [newScreeningChoices, setNewScreeningChoices] = useState([createEmptyScreeningChoice()])

  const mapToScreeningChoices = useCallback(
    (choicesArr = [], rulesArr = []) =>
      choicesArr.map((choice) => {
        const rule = rulesArr.find((r) => r.choice === choice)
        return {
          text: choice,
          screenOut: !!rule,
          screenOutText: rule?.screenoutMessage || rule?.screenOutText || ''
        }
      }),
    []
  )

  // Rating
  const [newRatingMin, setNewRatingMin] = useState(1)
  const [newRatingMax, setNewRatingMax] = useState(5)
  const [newRatingName, setNewRatingName] = useState('')

  // AI-Moderator
  const [newAiPrompt, setNewAiPrompt] = useState('')
  const [newAiCount, setNewAiCount] = useState(1)

  const [newCards, setNewCards] = useState([]); // [{ text: '', imageUrl: '' }, ...]
  const [newGroups, setNewGroups] = useState([]); // ['Grup A', 'Grup B', ...]

  const createEmptyPictureChoice = () => ({ label: '', imageUrl: '' })
  const [newPictureChoices, setNewPictureChoices] = useState([createEmptyPictureChoice()])
  const [newPictureAllowMultiple, setNewPictureAllowMultiple] = useState(false)
  const [newPictureAllowOther, setNewPictureAllowOther] = useState(false)
  const [newMaskedType, setNewMaskedType] = useState('number')
  const [newInfoPlacement, setNewInfoPlacement] = useState('inline')
  const handleNewQuestionTextChange = useCallback((val) => setNewQuestionText(val), [])
 

  const questionTypeOptions = useMemo(
    () => [
      {
        value: 'openEnded',
        label: t('moderatorFormBuilder.openEnded'),
        icon: <TextFieldsIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.openEnded
      },
      {
        value: 'singleChoice',
        label: t('moderatorFormBuilder.singleChoice'),
        icon: <RadioButtonCheckedIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.singleChoice
      },
      {
        value: 'multipleChoice',
        label: t('moderatorFormBuilder.multipleChoice'),
        icon: <CheckBoxIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.multipleChoice
      },
      {
        value: 'pictureChoice',
        label: t('moderatorFormBuilder.pictureChoice'),
        icon: <ImageIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.pictureChoice
      },
      {
        value: 'masked',
        label: t('moderatorFormBuilder.maskedQuestion'),
        icon: <MaskIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.masked
      },
      {
        value: 'screening',
        label: t('moderatorFormBuilder.screeningQuestion'),
        icon: <FilterAltIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.screening
      },
      {
        value: 'ratingQuestion',
        label: t('moderatorFormBuilder.ratingQuestion'),
        icon: <StarRateIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.ratingQuestion
      },
      {
        value: 'videoQuestion',
        label: t('moderatorFormBuilder.videoQuestion'),
        icon: <OndemandVideoIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.videoQuestion
      },
      {
        value: 'photoCollage',
        label: t('moderatorFormBuilder.photoCollage'),
        icon: <ImageIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.photoCollage
      },
      {
        value: 'aiModerator',
        label: t('moderatorFormBuilder.aiModerator'),
        icon: <SmartToyIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.aiModerator
      },
      {
        value: 'infoQuestion',
        label: t('moderatorFormBuilder.infoQuestion'),
        icon: <InfoIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.infoQuestion
      },
      {
        value: 'cardSorting',
        label: t('moderatorFormBuilder.cardSorting'),
        icon: <ViewColumnIcon fontSize="small" />,
        palette: QUESTION_TYPE_PALETTES.cardSorting
      }
    ],
    [t]
  )

  const questionTypeLabelMap = useMemo(
    () =>
      questionTypeOptions.reduce((acc, option) => {
        acc[option.value] = option.label
        return acc
      }, {}),
    [questionTypeOptions]
  )

  const languageOptions = useMemo(
    () => [
  
      { value: 'en', label: t('moderatorFormBuilder.languageOptions.en') },
      { value: 'de', label: t('moderatorFormBuilder.languageOptions.de') },
      { value: 'fr', label: t('moderatorFormBuilder.languageOptions.fr') },
      { value: 'tr', label: t('moderatorFormBuilder.languageOptions.tr') },
      { value: 'el', label: t('moderatorFormBuilder.languageOptions.el') },
      { value: 'nl', label: t('moderatorFormBuilder.languageOptions.nl') },
      { value: 'es', label: t('moderatorFormBuilder.languageOptions.es') },
      { value: 'it', label: t('moderatorFormBuilder.languageOptions.it') }
    ],
    [t]
  )

  const getQuestionTypeLabel = useCallback(
    (type) => questionTypeLabelMap[type] || type,
    [questionTypeLabelMap]
  )

  const getQuestionPreview = useCallback((html) => {
    if (!html) return ''
    // Fallback for non-DOM environments (shouldn't happen in this view but keeps it safe)
    if (typeof document === 'undefined') {
      const textOnly = html.replace(/<[^>]+>/g, ' ')
      return textOnly.split('\n').find((line) => line.trim())?.trim() || ''
    }
    const temp = document.createElement('div')
    temp.innerHTML = html
    const textContent = temp.textContent || temp.innerText || ''
    return textContent.split('\n').find((line) => line.trim())?.trim() || ''
  }, [])

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Yeni dialog state
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    form: null,
    newName: '',
    newLanguage: 'tr',
    newRedirectEnabled: false,
    newRedirectUrl: '',
    newShowNavigationArrows: true,
    newShowProgressBar: true,
    newShowQuestionNumber: true
  })

  const [newFormRedirectEnabled, setNewFormRedirectEnabled] = useState(false)
  const [newFormRedirectUrl, setNewFormRedirectUrl] = useState('')
  const [newFormShowNavigationArrows, setNewFormShowNavigationArrows] = useState(true)
  const [newFormShowProgressBar, setNewFormShowProgressBar] = useState(true)
  const [newFormShowQuestionNumber, setNewFormShowQuestionNumber] = useState(true)

  const formStats = useMemo(
    () => ({
      total: questionForms.length,
      draft: questionForms.filter((form) => form.status === 'draft' || !form.status).length,
      published: questionForms.filter((form) => form.status === 'published').length,
      completed: questionForms.filter((form) => form.status === 'completed').length
    }),
    [questionForms]
  )

  // Arama & Durum filtresi uygulanmış liste
  const filteredForms = questionForms.filter((form) => {
    const matchesSearch = searchTerm
      ? (form.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      : true

    const matchesStatus = statusFilter
      ? form.status === statusFilter
      : true

    return matchesSearch && matchesStatus
  })

  const getStatusMeta = useCallback(
    (status) => {
      switch (status) {
        case 'published':
          return {
            label: t('moderatorFormBuilder.formPublishedLabel'),
            color: 'info',
            bg: '#bfdbfe',
            textColor: '#0f172a'
          }
        case 'completed':
          return {
            label: t('moderatorFormBuilder.formCompletedLabel'),
            color: 'success',
            bg: '#bbf7d0',
            textColor: '#0f172a'
          }
        default:
          return {
            label: t('moderatorFormBuilder.draft'),
            color: 'warning',
            bg: '#fed7aa',
            textColor: '#7c2d12'
          }
      }
    },
    [t]
  )

  const generatePublicShareId = useCallback(
    () => `f-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`,
    []
  )

  // Seçili formu Firestore'dan gelen son hal ile güncel tutalım (özellikle paylaşım linki için)
  useEffect(() => {
    if (!selectedQuestionForm) return
    const latest = questionForms.find((f) => f.id === selectedQuestionForm.id)
    if (!latest) return

    const hasDiff =
      latest.status !== selectedQuestionForm.status ||
      latest.name !== selectedQuestionForm.name ||
      latest.publicShareId !== selectedQuestionForm.publicShareId

    if (hasDiff) {
      setSelectedQuestionForm(latest)
    }
  }, [questionForms, selectedQuestionForm])

  // Rename dialog açma
  const handleOpenRenameDialog = (form) => {
    setRenameDialog({
      open: true,
      form,
      newName: form.name || '',
      newLanguage: form.language || 'tr',
      newRedirectEnabled: !!form.redirectEnabled,
      newRedirectUrl: form.redirectUrl || '',
      newShowNavigationArrows: form.showNavigationArrows !== false,
      newShowProgressBar: form.showProgressBar !== false,
      newShowQuestionNumber: form.showQuestionNumber !== false
    })
  }

  // Kaydetme
  const handleSaveRename = async () => {
    if (!renameDialog.newName.trim()) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    if (renameDialog.newRedirectEnabled && !renameDialog.newRedirectUrl.trim()) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.redirectUrlRequired'),
        severity: 'warning'
      })
      return
    }
    try {
      await updateDoc(doc(db, 'questionForms', renameDialog.form.id), {
        name: renameDialog.newName.trim(),
        language: renameDialog.newLanguage || 'tr',
        redirectEnabled: !!renameDialog.newRedirectEnabled,
        redirectUrl: renameDialog.newRedirectEnabled
          ? renameDialog.newRedirectUrl.trim()
          : '',
        showNavigationArrows: renameDialog.newShowNavigationArrows !== false,
        showProgressBar: renameDialog.newShowProgressBar !== false,
        showQuestionNumber: renameDialog.newShowQuestionNumber !== false
      })
      setRenameDialog({
        open: false,
        form: null,
        newName: '',
        newLanguage: 'tr',
        newRedirectEnabled: false,
        newRedirectUrl: '',
        newShowNavigationArrows: true,
        newShowProgressBar: true,
        newShowQuestionNumber: true
      })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameUpdateSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Form adı güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameUpdateError'),
        severity: 'error'
      })
    }
  }

  // İptal
  const handleCancelRename = () => {
    setRenameDialog({
      open: false,
      form: null,
      newName: '',
      newLanguage: 'tr',
      newRedirectEnabled: false,
      newRedirectUrl: '',
      newShowNavigationArrows: true,
      newShowProgressBar: true,
      newShowQuestionNumber: true
    })
  }

  const ensureShareLink = async (form) => {
    const shareId = form.publicShareId || generatePublicShareId()
    if (!form.publicShareId) {
      await updateDoc(doc(db, 'questionForms', form.id), { publicShareId: shareId })
      setSelectedQuestionForm((prev) =>
        prev && prev.id === form.id ? { ...prev, publicShareId: shareId } : prev
      )
    }
    return shareId
  }

  const handleCopyShareLink = async (form) => {
    try {
      const shareId = await ensureShareLink(form)
      const shareUrl = `${window.location.origin}/#/forms/${shareId}`
      await navigator.clipboard.writeText(shareUrl)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.shareLinkCopied'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Share link error:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.shareLinkCopyError'),
        severity: 'error'
      })
    }
  }

  const handlePreviewForm = (form) => {
    const params = new URLSearchParams({
      questionFormId: form.id,
      channelId: form.channelId || 'global',
      preview: '1'
    })
    const path = `/user/answers?${params.toString()}`
    const fullUrl = `${window.location.origin}/#${path}`
    window.open(fullUrl, '_blank', 'noopener,noreferrer')
  }

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }))
  }

  const [librarySaveDialog, setLibrarySaveDialog] = useState({
    open: false,
    form: null,
    name: ''
  })

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null
  })

  const openConfirmDialog = useCallback((message, onConfirm) => {
    setConfirmDialog({
      open: true,
      message,
      onConfirm
    })
  }, [])

  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialog({
      open: false,
      message: '',
      onConfirm: null
    })
  }, [])

  const handleConfirmDialogConfirm = useCallback(async () => {
    const { onConfirm } = confirmDialog
    handleCloseConfirmDialog()
    if (onConfirm) {
      await onConfirm()
    }
  }, [confirmDialog, handleCloseConfirmDialog])

  // Edit Dialog
  const [editDialog, setEditDialog] = useState({
    open: false,
    question: null,
    updatedText: '',
    updatedType: 'openEnded',
    updatedChoices: [],
    updatedAllowOther: false,
    updatedRequireMedia: false,
    updatedRequired: false,
    updatedRatingMin: 1,
    updatedRatingMax: 5,
    updatedRatingName: '',
    updatedAiPrompt: '',
    updatedAiCount: 1,
    updatedCards: [],
    updatedGroups: [],
    updatedScreeningMode: 'single',
    updatedScreeningChoices: [],
    updatedPictureChoices: [],
    updatedPictureAllowMultiple: false,
    updatedPictureAllowOther: false,
    updatedMaskedType: 'number',
    updatedInfoPlacement: 'inline'
  })
  const handleEditQuestionTextChange = useCallback((val) => {
    setEditDialog((prev) => ({ ...prev, updatedText: val }))
  }, [])

  useEffect(() => {
    if (!editDialog.open) return
    if (editDialog.updatedType !== 'screening') return
    if (editDialog.updatedScreeningChoices && editDialog.updatedScreeningChoices.length > 0) return

    setEditDialog((prev) => ({
      ...prev,
      updatedScreeningChoices: mapToScreeningChoices(
        prev.updatedChoices || [],
        prev.question?.screeningRules || []
      )
    }))
  }, [editDialog.open, editDialog.updatedType, editDialog.updatedScreeningChoices, mapToScreeningChoices])

  // ------------------ Soru Formu Listesi ------------------
  useEffect(() => {
    if (!currentUser) return

    setLoadingQuestionForms(true)
    const formsRef = collection(db, 'questionForms')
    let qForms

    if (organization?.id) {
      qForms = query(
        formsRef,
        where('organizationId', '==', organization.id),
        orderBy('createdAt', 'desc')
      )
    } else if (userRole === 'admin') {
      qForms = query(
        formsRef,
        where('createdBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      )
    } else {
      qForms = query(
        formsRef,
        where('createdBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      )
    }

    const unsubscribe = onSnapshot(
      qForms,
      (snapshot) => {
        const forms = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        setQuestionForms(forms)
        setLoadingQuestionForms(false)
      },
      (error) => {
        console.error('Soru formları alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.questionFormsLoadingError'),
          severity: 'error'
        })
        setLoadingQuestionForms(false)
      }
    )

    return () => unsubscribe()
  }, [currentUser, userRole, organization, t])

  useEffect(() => {
    if (!currentUser?.uid || !organization?.id) return

    const attachOrganizationToLegacyForms = async () => {
      try {
        const legacyFormsSnap = await getDocs(
          query(collection(db, 'questionForms'), where('createdBy', '==', currentUser.uid))
        )
        const updates = legacyFormsSnap.docs
          .filter((docSnap) => !docSnap.data().organizationId)
          .map((docSnap) =>
            updateDoc(doc(db, 'questionForms', docSnap.id), { organizationId: organization.id })
          )
        if (updates.length) {
          await Promise.all(updates)
        }
      } catch (error) {
        console.error('Organization field backfill error:', error)
      }
    }

    attachOrganizationToLegacyForms()
  }, [currentUser?.uid, organization?.id])

  // ------------------ Soru Listesi ------------------
  useEffect(() => {
    if (!selectedQuestionForm) return

    setLoadingQuestions(true)
    const qQuestions = query(
      collection(db, 'questions'),
      where('questionFormId', '==', selectedQuestionForm.id),
      orderBy('sortOrder', 'asc')
    )

    const unsubscribe = onSnapshot(
      qQuestions,
      (snapshot) => {
        const qs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        setQuestions(qs)
        setLoadingQuestions(false)
      },
      (error) => {
        console.error('Sorular alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.questionsLoadingError'),
          severity: 'error'
        })
        setLoadingQuestions(false)
      }
    )

    return () => unsubscribe()
  }, [selectedQuestionForm, t])

  // Form değiştiğinde yeni soru alanını sıfırla
  useEffect(() => {
    if (!selectedQuestionForm) return

    setNewQuestionText('')
    setNewQuestionType('openEnded')
    setNewChoices([''])
    setNewAllowOther(false)
    setNewRequireMedia(false)
    setNewRequired(false)
    setNewRatingMin(1)
    setNewRatingMax(5)
    setNewRatingName('')
    setNewAiPrompt('')
    setNewAiCount(1)
    setNewPictureChoices([createEmptyPictureChoice()])
    setNewPictureAllowMultiple(false)
    setNewPictureAllowOther(false)
    setNewMaskedType('number')
    setNewInfoPlacement('inline')
    setNewScreeningMode('single')
    setNewScreeningChoices([createEmptyScreeningChoice()])
    setNewCards([])
    setNewGroups([])
  }, [selectedQuestionForm])

  // =======================================================
  //                     Soru Formu İşlemleri
  // =======================================================
  const handleCreateQuestionForm = async () => {
    const trimmedName = newFormName.trim()
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameEmpty'),
        severity: 'warning'
      })
      return
    }

    if (!organization?.id) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.organizationLoadError'),
        severity: 'error'
      })
      return
    }

    if (newFormRedirectEnabled && !newFormRedirectUrl.trim()) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.redirectUrlRequired'),
        severity: 'warning'
      })
      return
    }

    try {
      await addDoc(collection(db, 'questionForms'), {
        channelId: 'global',
        name: trimmedName,
        createdBy: currentUser.uid,
        organizationId: organization.id,
        createdAt: serverTimestamp(),
        status: 'draft',
        publicShareId: generatePublicShareId(),
        language: newFormLanguage || 'tr',
        redirectEnabled: !!newFormRedirectEnabled,
        redirectUrl: newFormRedirectEnabled ? newFormRedirectUrl.trim() : '',
        showNavigationArrows: newFormShowNavigationArrows,
        showProgressBar: newFormShowProgressBar,
        showQuestionNumber: newFormShowQuestionNumber
      })
      setNewFormName('')
      setNewFormLanguage('tr')
      setNewFormRedirectEnabled(false)
      setNewFormRedirectUrl('')
      setNewFormShowNavigationArrows(true)
      setNewFormShowProgressBar(true)
      setNewFormShowQuestionNumber(true)
      setAddFormDialogOpen(false)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formAddedDraft'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Form eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formAddError'),
        severity: 'error'
      })
    }
  }

  const handleDeleteQuestionForm = (formId) => {
    openConfirmDialog(t('moderatorFormBuilder.deleteFormConfirm'), async () => {
      try {
        await deleteDoc(doc(db, 'questionForms', formId))
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.formDeleted'),
          severity: 'success'
        })
        if (selectedQuestionForm && selectedQuestionForm.id === formId) {
          setSelectedQuestionForm(null)
        }
      } catch (error) {
        console.error('Form silinirken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.formDeleteError'),
          severity: 'error'
        })
      }
    })
  }

  const handleDuplicateForm = async (form) => {
    try {
      const questionsSnap = await getDocs(
        query(
          collection(db, 'questions'),
          where('questionFormId', '==', form.id),
          orderBy('sortOrder', 'asc')
        )
      )

      const questionsToCopy = questionsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }))

      const copySuffix = t('moderatorFormBuilder.formCopySuffix', '(Kopya)')
      const newFormRef = await addDoc(collection(db, 'questionForms'), {
        channelId: form.channelId || 'global',
        name: `${form.name || 'Form'} ${copySuffix}`,
        createdBy: currentUser.uid,
        organizationId: organization?.id || form.organizationId || null,
        createdAt: serverTimestamp(),
        status: 'draft',
        publicShareId: generatePublicShareId(),
        language: form.language || 'tr',
        redirectEnabled: !!form.redirectEnabled,
        redirectUrl: form.redirectEnabled ? form.redirectUrl || '' : '',
        showNavigationArrows: form.showNavigationArrows ?? true,
        showProgressBar: form.showProgressBar ?? true,
        showQuestionNumber: form.showQuestionNumber ?? true
      })

      const questionPromises = questionsToCopy.map((q, index) =>
        addDoc(collection(db, 'questions'), {
          channelId: form.channelId || q.channelId || 'global',
          questionFormId: newFormRef.id,
          organizationId: organization?.id || form.organizationId || null,
          text: q.text || '',
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          questionType: q.questionType || 'openEnded',
          cards: q.questionType === 'cardSorting' ? q.cards || [] : [],
          groups: q.questionType === 'cardSorting' ? q.groups || [] : [],
          choices: q.choices || [],
          allowOther: q.questionType === 'screening' ? false : q.allowOther || false,
          sortOrder: index,
          requireMedia: q.requireMedia || false,
          isRequired: q.isRequired || false,
          ratingMin: q.ratingMin || 1,
          ratingMax: q.ratingMax || 5,
          ratingName: q.ratingName || '',
          aiPrompt: q.aiPrompt || '',
          aiCount: q.aiCount || 1,
          maskedType: q.questionType === 'masked' ? q.maskedType || 'number' : null,
          pictureChoices: q.questionType === 'pictureChoice' ? q.pictureChoices || [] : [],
          pictureAllowMultiple:
            q.questionType === 'pictureChoice' ? q.pictureAllowMultiple || false : false,
          pictureAllowOther:
            q.questionType === 'pictureChoice' ? q.pictureAllowOther || false : false,
          screeningMode: q.questionType === 'screening' ? q.screeningMode || 'single' : null,
          screeningRules: q.questionType === 'screening' ? q.screeningRules || [] : [],
          infoPlacement: q.questionType === 'infoQuestion' ? q.infoPlacement || 'inline' : null
        })
      )

      await Promise.all(questionPromises)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.duplicateFormSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Form kopyalanırken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.duplicateFormError'),
        severity: 'error'
      })
    }
  }

  // Form Durum Güncellemeleri
  async function handlePublish(form) {
    try {
      const shareId = form.publicShareId || generatePublicShareId()
      await updateDoc(doc(db, 'questionForms', form.id), {
        status: 'published',
        publicShareId: shareId
      })
      setSelectedQuestionForm((prev) =>
        prev && prev.id === form.id ? { ...prev, publicShareId: shareId } : prev
      )
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishSuccess', { formName: form.name }),
        severity: 'success'
      })
    } catch (error) {
      console.error(error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishError'),
        severity: 'error'
      })
    }
  }

  function handleMarkComplete(form) {
    openConfirmDialog(t('moderatorFormBuilder.completeConfirm'), async () => {
      try {
        await updateDoc(doc(db, 'questionForms', form.id), { status: 'completed' })
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.completeSuccess', { formName: form.name }),
          severity: 'success'
        })
      } catch (error) {
        console.error(error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.completeError'),
          severity: 'error'
        })
      }
    })
  }

  function handleRevertToDraft(form) {
    openConfirmDialog(t('moderatorFormBuilder.revertConfirm'), async () => {
      try {
        // 1) Tüm responses sil
        const respQuery = query(
          collection(db, 'responses'),
          where('questionFormId', '==', form.id)
        )
        const respSnap = await getDocs(respQuery)
        const deletes = []
        respSnap.forEach((docSnap) => {
          deletes.push(deleteDoc(doc(db, 'responses', docSnap.id)))
        })
        await Promise.all(deletes)

        // 2) draft’a geri al
        await updateDoc(doc(db, 'questionForms', form.id), { status: 'draft' })
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.revertSuccess', { formName: form.name }),
          severity: 'success'
        })
      } catch (error) {
        console.error(error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.revertError'),
          severity: 'error'
        })
      }
    })
  }


// Kart İşleyicileri
const handleAddCard = () => {
  setNewCards([...newCards, { text: '', imageUrl: '' }]);
};

const handleRemoveCard = (index) => {
  const updated = newCards.filter((_, i) => i !== index);
  setNewCards(updated);
};

const handleCardTextChange = (index, value) => {
  const updated = [...newCards];
  updated[index].text = value;
  setNewCards(updated);
};

const handleUploadCardImage = async (index) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;
    try {
      const url = await uploadFile(file);
      const updated = [...newCards];
      updated[index].imageUrl = url;
      setNewCards(updated);
    } catch (err) {
      console.error('Fotoğraf yükleme hatası:', err);
      setSnackbar({
        open: true,
        message:  t('moderatorFormBuilder.photoUploadError'),
        severity: 'error'
      });
    }
  };
  input.click();
};

// Grup İşleyicileri
const handleAddGroup = () => {
  setNewGroups([...newGroups, '']);
};

const handleRemoveGroup = (index) => {
  const updated = newGroups.filter((_, i) => i !== index);
  setNewGroups(updated);
};

const handleGroupChange = (index, value) => {
  const updated = [...newGroups];
  updated[index] = value;
  setNewGroups(updated);
};

  // =======================================================
  //                      Soru İşlemleri
  // =======================================================
  const handleAddQuestion = async () => {
    if (!selectedQuestionForm) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.selectFormWarning'),
        severity: 'warning'
      })
      return
    }
    if (selectedQuestionForm.status !== 'draft') {
      // Bu form yayınlanmış veya tamamlanmış.
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionError'), // isteğe bağlı ek key
        severity: 'warning'
      })
      return
    }

    // Soru metni boş mu?
    const plainText = (newQuestionText || '').replace(/<[^>]+>/g, '').trim()
    if (!plainText) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionTextEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    let trimmedChoices = newChoices.map((c) => c.trim()).filter((c) => c !== '')
    let screeningRules = []
    let pictureChoicesPayload = []
    let maskedTypeValue = newMaskedType

    if (newQuestionType === 'screening') {
      trimmedChoices = newScreeningChoices.map((c) => c.text.trim()).filter((c) => c !== '')
      screeningRules = newScreeningChoices
        .filter((c) => c.screenOut && c.text.trim())
        .map((c) => ({
          choice: c.text.trim(),
          screenoutMessage: c.screenOutText?.trim() || ''
        }))
    }

    if (newQuestionType === 'pictureChoice') {
      pictureChoicesPayload = newPictureChoices
        .map((c) => ({ label: c.label.trim(), imageUrl: c.imageUrl }))
        .filter((c) => c.label && c.imageUrl)
      trimmedChoices = pictureChoicesPayload.map((c) => c.label)
    }

    if (newQuestionType === 'masked') {
      trimmedChoices = []
      if (!maskedTypeValue) maskedTypeValue = 'number'
    }

    if (
      (newQuestionType === 'singleChoice' || newQuestionType === 'multipleChoice' ||
        newQuestionType === 'screening' || newQuestionType === 'pictureChoice') &&
      trimmedChoices.length === 0
    ) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.choicesRequiredWarning'),
        severity: 'warning'
      })
      return
    }

    if (newQuestionType === 'screening' && screeningRules.length === 0) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.screeningRuleWarning'),
        severity: 'warning'
      })
      return
    }

    if (newQuestionType === 'pictureChoice') {
      const invalidImage = newPictureChoices.some((c) => c.label.trim() && !c.imageUrl)
      if (invalidImage) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.pictureChoiceImageWarning'),
          severity: 'warning'
        })
        return
      }
    }

    // Rating question
    if (newQuestionType === 'ratingQuestion') {
      if (!newRatingName.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingNameWarning'),
          severity: 'warning'
        })
        return
      }
      if (newRatingMin >= newRatingMax) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingMinMaxWarning'),
          severity: 'warning'
        })
        return
      }
    }

    // AI-Moderator question
    if (newQuestionType === 'aiModerator') {
      if (!newAiPrompt.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiPromptEmptyWarning'),
          severity: 'warning'
        })
        return
      }
      if (newAiCount < 1) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiCountMinWarning'),
          severity: 'warning'
        })
        return
      }
    }

    if (newQuestionType === 'cardSorting') {
      if (newCards.length === 0 || newGroups.length === 0) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.cardSortingError'),
          severity: 'warning'
        });
        return;
      }
    }

    try {
      const sortOrderValue = questions.length
      await addDoc(collection(db, 'questions'), {
        channelId: selectedQuestionForm.channelId || 'global',
        questionFormId: selectedQuestionForm.id,
        organizationId: selectedQuestionForm.organizationId || organization?.id || null,
        text: newQuestionText,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        questionType: newQuestionType,
        cards: newQuestionType === 'cardSorting' ? newCards : [],
        groups: newQuestionType === 'cardSorting' ? newGroups : [],
        choices: trimmedChoices,
        allowOther: newQuestionType === 'screening' ? false : newAllowOther,
        sortOrder: sortOrderValue,
        requireMedia: newQuestionType === 'openEnded' ? newRequireMedia : false,
        isRequired: newRequired,
        ratingMin: newRatingMin,
        ratingMax: newRatingMax,
        ratingName: newRatingName,
        aiPrompt: newAiPrompt,
        aiCount: newAiCount,
        screeningMode: newQuestionType === 'screening' ? newScreeningMode : null,
        screeningRules: newQuestionType === 'screening' ? screeningRules : [],
        pictureChoices: newQuestionType === 'pictureChoice' ? pictureChoicesPayload : [],
        pictureAllowMultiple: newQuestionType === 'pictureChoice' ? newPictureAllowMultiple : false,
        pictureAllowOther: newQuestionType === 'pictureChoice' ? newPictureAllowOther : false,
        maskedType: newQuestionType === 'masked' ? maskedTypeValue : null,
        infoPlacement: newQuestionType === 'infoQuestion' ? newInfoPlacement : null
      })

      // Temizle
      setNewQuestionText('')
      setNewQuestionType('openEnded')
      setNewChoices([''])
      setNewAllowOther(false)
      setNewRequireMedia(false)
      setNewRequired(false)
      setNewRatingMin(1)
      setNewRatingMax(5)
      setNewRatingName('')
      setNewAiPrompt('')
      setNewAiCount(1)
      setNewPictureChoices([createEmptyPictureChoice()])
      setNewPictureAllowMultiple(false)
      setNewPictureAllowOther(false)
      setNewMaskedType('number')
      setNewInfoPlacement('inline')
      setNewScreeningMode('single')
      setNewScreeningChoices([createEmptyScreeningChoice()])
      setNewCards([]);
      setNewGroups([]);

      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.addQuestionError'),
        severity: 'error'
      })
    }
  }

  const handleEditQuestion = (question) => {
    setEditDialog({
      open: true,
      question,
      updatedText: question.text || '',
      updatedType: question.questionType || 'openEnded',
      updatedChoices: question.choices || [],
      updatedAllowOther: question.allowOther || false,
      updatedRequireMedia: question.requireMedia || false,
      updatedRequired: question.isRequired || false,
      updatedRatingMin: question.ratingMin || 1,
      updatedRatingMax: question.ratingMax || 5,
      updatedRatingName: question.ratingName || '',
      updatedAiPrompt: question.aiPrompt || '',
      updatedAiCount: question.aiCount || 1,
      updatedCards: question.cards || [],
      updatedGroups: question.groups || [],
      updatedScreeningMode: question.screeningMode || 'single',
      updatedScreeningChoices: mapToScreeningChoices(
        question.choices || [],
        question.screeningRules || []
      ),
      updatedPictureChoices: question.pictureChoices || [],
      updatedPictureAllowMultiple: question.pictureAllowMultiple || false,
      updatedPictureAllowOther: question.pictureAllowOther || false,
      updatedMaskedType: question.maskedType || 'number',
      updatedInfoPlacement: question.infoPlacement || 'inline'
    })
  }

  const handleSaveEdit = async () => {
    if (selectedQuestionForm?.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'), // isteğe bağlı
        severity: 'warning'
      })
      setEditDialog((prev) => ({ ...prev, open: false }))
      return
    }

    const {
      question,
      updatedText,
      updatedType,
      updatedChoices,
      updatedAllowOther,
      updatedRequireMedia,
      updatedRequired,
      updatedRatingMin,
      updatedRatingMax,
      updatedRatingName,
      updatedAiPrompt,
      updatedAiCount,
      updatedCards,
      updatedGroups,
      updatedScreeningMode,
      updatedScreeningChoices,
      updatedPictureChoices,
      updatedPictureAllowMultiple,
      updatedPictureAllowOther,
      updatedMaskedType,
      updatedInfoPlacement
    } = editDialog

    const plainText = (updatedText || '').replace(/<[^>]+>/g, '').trim()
    if (!plainText) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionTextEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    let trimmedChoices = updatedChoices.map((c) => c.trim()).filter((c) => c !== '')
    let screeningRules = []
    let pictureChoicesPayload = []
    let maskedTypeValue = updatedMaskedType

    if (updatedType === 'screening') {
      trimmedChoices = updatedScreeningChoices.map((c) => c.text.trim()).filter((c) => c !== '')
      screeningRules = updatedScreeningChoices
        .filter((c) => c.screenOut && c.text.trim())
        .map((c) => ({ choice: c.text.trim(), screenoutMessage: c.screenOutText?.trim() || '' }))
    }

    if (updatedType === 'pictureChoice') {
      pictureChoicesPayload = (updatedPictureChoices || [])
        .map((c) => ({ label: (c.label || '').trim(), imageUrl: c.imageUrl }))
        .filter((c) => c.label && c.imageUrl)
      trimmedChoices = pictureChoicesPayload.map((c) => c.label)
    }

    if (updatedType === 'masked') {
      trimmedChoices = []
      maskedTypeValue = maskedTypeValue || 'number'
    }

    if (
      (updatedType === 'singleChoice' || updatedType === 'multipleChoice' || updatedType === 'screening' || updatedType === 'pictureChoice') &&
      trimmedChoices.length === 0
    ) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.choicesRequiredWarning'),
        severity: 'warning'
      })
      return
    }

    if (updatedType === 'screening' && screeningRules.length === 0) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.screeningRuleWarning'),
        severity: 'warning'
      })
      return
    }

    if (updatedType === 'pictureChoice') {
      const invalidImage = (updatedPictureChoices || []).some(
        (c) => (c.label || '').trim() && !c.imageUrl
      )
      if (invalidImage) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.pictureChoiceImageWarning'),
          severity: 'warning'
        })
        return
      }
    }

    if (updatedType === 'ratingQuestion') {
      if (!updatedRatingName.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingNameWarning'),
          severity: 'warning'
        })
        return
      }
      if (updatedRatingMin >= updatedRatingMax) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.ratingMinMaxWarning'),
          severity: 'warning'
        })
        return
      }
    }

    if (updatedType === 'aiModerator') {
      if (!updatedAiPrompt.trim()) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiPromptEmptyWarning'),
          severity: 'warning'
        })
        return
      }
      if (updatedAiCount < 1) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.aiCountMinWarning'),
          severity: 'warning'
        })
        return
      }
    }

    try {
      await updateDoc(doc(db, 'questions', question.id), {
        text: updatedText,
        questionType: updatedType,
        choices: trimmedChoices,
        allowOther: updatedType === 'screening' ? false : updatedAllowOther,
        requireMedia: updatedType === 'openEnded' ? updatedRequireMedia : false,
        isRequired: updatedRequired,
        ratingMin: updatedRatingMin,
        ratingMax: updatedRatingMax,
        ratingName: updatedRatingName,
        aiPrompt: updatedAiPrompt,
        aiCount: updatedAiCount,
        screeningMode: updatedType === 'screening' ? updatedScreeningMode : null,
        screeningRules: updatedType === 'screening' ? screeningRules : [],
        pictureChoices: updatedType === 'pictureChoice' ? pictureChoicesPayload : [],
        pictureAllowMultiple: updatedType === 'pictureChoice' ? updatedPictureAllowMultiple : false,
        pictureAllowOther: updatedType === 'pictureChoice' ? updatedPictureAllowOther : false,
        maskedType: updatedType === 'masked' ? maskedTypeValue : null,
        infoPlacement: updatedType === 'infoQuestion' ? updatedInfoPlacement : null,
        ...(updatedType === 'cardSorting'
          ? {
              cards: updatedCards,
              groups: updatedGroups
            }
          : {})
      })

      setEditDialog({
        open: false,
        question: null,
        updatedText: '',
        updatedType: 'openEnded',
        updatedChoices: [],
        updatedAllowOther: false,
        updatedRequireMedia: false,
        updatedRequired: false,
        updatedRatingMin: 1,
        updatedRatingMax: 5,
        updatedRatingName: '',
        updatedAiPrompt: '',
        updatedAiCount: 1,
        updatedScreeningMode: 'single',
        updatedScreeningChoices: [],
        updatedPictureChoices: [],
        updatedPictureAllowMultiple: false,
        updatedPictureAllowOther: false,
        updatedMaskedType: 'number',
        updatedInfoPlacement: 'inline'
      })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.updateQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.updateQuestionError'),
        severity: 'error'
      })
    }
  }

  const handleDeleteQuestion = (questionId) => {
    if (selectedQuestionForm?.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'),
        severity: 'warning'
      })
      return
    }
    openConfirmDialog(t('moderatorFormBuilder.deleteQuestionConfirm'), async () => {
      try {
        await deleteDoc(doc(db, 'questions', questionId))
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.deleteQuestionSuccess'),
          severity: 'success'
        })
      } catch (error) {
        console.error('Soru silinirken hata:', error)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.deleteQuestionError'),
          severity: 'error'
        })
      }
    })
  }

  const handleDuplicateQuestion = async (question) => {
    if (selectedQuestionForm?.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'),
        severity: 'warning'
      })
      return
    }

    try {
      const sortOrderValue = questions.length
      await addDoc(collection(db, 'questions'), {
        channelId: selectedQuestionForm.channelId || question.channelId || 'global',
        questionFormId: selectedQuestionForm.id,
        organizationId: selectedQuestionForm.organizationId || organization?.id || null,
        text: question.text || '',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        questionType: question.questionType || 'openEnded',
        cards: question.questionType === 'cardSorting' ? question.cards || [] : [],
        groups: question.questionType === 'cardSorting' ? question.groups || [] : [],
        choices: question.choices || [],
        allowOther: question.questionType === 'screening' ? false : question.allowOther || false,
        sortOrder: sortOrderValue,
        requireMedia: question.requireMedia || false,
        isRequired: question.isRequired || false,
        ratingMin: question.ratingMin || 1,
        ratingMax: question.ratingMax || 5,
        ratingName: question.ratingName || '',
        aiPrompt: question.aiPrompt || '',
        aiCount: question.aiCount || 1,
        maskedType: question.questionType === 'masked' ? question.maskedType || 'number' : null,
        pictureChoices: question.questionType === 'pictureChoice' ? question.pictureChoices || [] : [],
        pictureAllowMultiple: question.questionType === 'pictureChoice' ? question.pictureAllowMultiple || false : false,
        pictureAllowOther: question.questionType === 'pictureChoice' ? question.pictureAllowOther || false : false,
        screeningMode: question.questionType === 'screening' ? question.screeningMode || 'single' : null,
        screeningRules: question.questionType === 'screening' ? question.screeningRules || [] : [],
        infoPlacement: question.questionType === 'infoQuestion' ? question.infoPlacement || 'inline' : null
      })
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.duplicateQuestionSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Soru kopyalanırken hata:', error)
      setSnackbar({
        open: true,
        message: t(
          'moderatorFormBuilder.duplicateQuestionError'
        ),
        severity: 'error'
      })
    }
  }

  // ------------------ Soruların Sürükle-Bırak Sıralaması ------------------
  async function handleDragEnd(result) {
    if (!result.destination) return

    // Yayınlanan veya tamamlanan formlarda soru sırasını değiştirmeyi engelle
    if (!selectedQuestionForm || selectedQuestionForm.status !== 'draft') {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.publishedOrCompletedWarning'),
        severity: 'warning'
      })
      return
    }

    const reordered = Array.from(questions)
    const [movedItem] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, movedItem)
    setQuestions(reordered)

    try {
      for (let i = 0; i < reordered.length; i++) {
        const q = reordered[i]
        await updateDoc(doc(db, 'questions', q.id), {
          sortOrder: i
        })
      }
    } catch (err) {
      console.error('Sıra güncellenirken hata:', err)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.questionSortError'),
        severity: 'error'
      })
    }
  }

  // Yeni choice ekleme
  const handleAddChoice = () => {
    setNewChoices([...newChoices, ''])
  }
  const handleRemoveChoice = (index) => {
    const updated = [...newChoices]
    updated.splice(index, 1)
    setNewChoices(updated)
  }
  const handleChoiceChange = (index, value) => {
    const updated = [...newChoices]
    updated[index] = value
    setNewChoices(updated)
  }

  const handleAddPictureChoice = () => {
    setNewPictureChoices((prev) => [...prev, createEmptyPictureChoice()])
  }
  const handleRemovePictureChoice = (index) => {
    setNewPictureChoices((prev) => {
      const updated = [...prev]
      updated.splice(index, 1)
      return updated.length ? updated : [createEmptyPictureChoice()]
    })
  }
  const handlePictureChoiceLabelChange = (index, value) => {
    setNewPictureChoices((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], label: value }
      return updated
    })
  }
  const handlePictureChoiceUpload = async (index) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        setNewPictureChoices((prev) => {
          const updated = [...prev]
          updated[index] = { ...updated[index], imageUrl: url }
          return updated
        })
      } catch (err) {
        console.error('Görsel yüklenirken hata:', err)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.imageUploadError'),
          severity: 'error'
        })
      }
    }
    input.click()
  }

  const handleAddScreeningChoice = () => {
    setNewScreeningChoices((prev) => [...prev, createEmptyScreeningChoice()])
  }
  const handleRemoveScreeningChoice = (index) => {
    setNewScreeningChoices((prev) => {
      const updated = [...prev]
      updated.splice(index, 1)
      return updated.length > 0 ? updated : [createEmptyScreeningChoice()]
    })
  }
  const handleScreeningChoiceChange = (index, field, value) => {
    setNewScreeningChoices((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  // Edit dialog choices
  const handleEditAddChoice = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedChoices: [...prev.updatedChoices, '']
    }))
  }
  const handleEditRemoveChoice = (index) => {
    setEditDialog((prev) => {
      const updated = [...prev.updatedChoices]
      updated.splice(index, 1)
      return { ...prev, updatedChoices: updated }
    })
  }
  const handleEditChoiceChange = (index, value) => {
    setEditDialog((prev) => {
      const updated = [...prev.updatedChoices]
      updated[index] = value
      return { ...prev, updatedChoices: updated }
    })
  }

  const handleEditAddPictureChoice = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedPictureChoices: [...(prev.updatedPictureChoices || []), createEmptyPictureChoice()]
    }))
  }

  const handleEditRemovePictureChoice = (index) => {
    setEditDialog((prev) => {
      const updated = [...(prev.updatedPictureChoices || [])]
      updated.splice(index, 1)
      return {
        ...prev,
        updatedPictureChoices: updated.length ? updated : [createEmptyPictureChoice()]
      }
    })
  }

  const handleEditPictureChoiceLabelChange = (index, value) => {
    setEditDialog((prev) => {
      const updated = [...(prev.updatedPictureChoices || [])]
      updated[index] = { ...updated[index], label: value }
      return { ...prev, updatedPictureChoices: updated }
    })
  }

  const handleEditPictureChoiceUpload = (index) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        setEditDialog((prev) => {
          const updated = [...(prev.updatedPictureChoices || [])]
          updated[index] = { ...updated[index], imageUrl: url }
          return { ...prev, updatedPictureChoices: updated }
        })
      } catch (err) {
        console.error('Görsel yüklenirken hata:', err)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.imageUploadError'),
          severity: 'error'
        })
      }
    }
    input.click()
  }

  const handleEditAddScreeningChoice = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedScreeningChoices: [...(prev.updatedScreeningChoices || []), createEmptyScreeningChoice()]
    }))
  }

  const handleEditRemoveScreeningChoice = (index) => {
    setEditDialog((prev) => {
      const updated = [...(prev.updatedScreeningChoices || [])]
      updated.splice(index, 1)
      return {
        ...prev,
        updatedScreeningChoices: updated.length > 0 ? updated : [createEmptyScreeningChoice()]
      }
    })
  }

  const handleEditScreeningChoiceChange = (index, field, value) => {
    setEditDialog((prev) => {
      const updated = [...(prev.updatedScreeningChoices || [])]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, updatedScreeningChoices: updated }
    })
  }

  const handleSaveToLibrary = (formToSave = null) => {
    const targetForm = formToSave || selectedQuestionForm
    if (!targetForm) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.noFormOrQuestionsWarning'),
        severity: 'warning'
      })
      return
    }

    setLibrarySaveDialog({
      open: true,
      form: targetForm,
      name: targetForm.name || ''
    })
  }

  const handleCloseLibrarySaveDialog = () => {
    setLibrarySaveDialog({
      open: false,
      form: null,
      name: ''
    })
  }

  const handleConfirmLibrarySave = async () => {
    const targetForm = librarySaveDialog.form
    const libraryName = librarySaveDialog.name.trim()

    if (!targetForm) {
      handleCloseLibrarySaveDialog()
      return
    }

    if (!libraryName) {
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.formNameEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    try {
      let targetQuestions = questions

      if (targetForm.id !== selectedQuestionForm?.id) {
        const qsSnap = await getDocs(
          query(
            collection(db, 'questions'),
            where('questionFormId', '==', targetForm.id),
            orderBy('sortOrder', 'asc')
          )
        )
        targetQuestions = qsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      }

      if (!targetQuestions || targetQuestions.length === 0) {
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.noFormOrQuestionsWarning'),
          severity: 'warning'
        })
        handleCloseLibrarySaveDialog()
        return
      }

      const libraryForm = {
        name: libraryName,
        description: '',
        language: targetForm.language || 'tr',
        questions: targetQuestions.map((q) => ({
          text: q.text || '',
          questionType: q.questionType || 'openEnded',
          choices: q.choices || [],
          allowOther: q.allowOther || false,
          requireMedia: q.requireMedia || false,
          isRequired: q.isRequired || false,
          ratingMin: q.ratingMin || 1,
          ratingMax: q.ratingMax || 5,
          ratingName: q.ratingName || '',
          aiPrompt: q.aiPrompt || '',
          aiCount: q.aiCount || 1,
          cards: q.cards || [],
          groups: q.groups || [],
          infoPlacement: q.infoPlacement || 'inline'
        })),
        createdBy: currentUser.uid,
        createdAt: serverTimestamp()
      }

      await addDoc(collection(db, 'formLibrary'), libraryForm)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.savedToLibrarySuccess'),
        severity: 'success'
      })
      handleCloseLibrarySaveDialog()
    } catch (error) {
      console.error('Kütüphaneye kaydedilirken hata:', error)
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.savedToLibraryError'),
        severity: 'error'
      })
    }
  }

  // State ekleme
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [libraryForms, setLibraryForms] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // Kütüphane formlarını çekme
  useEffect(() => {
    if (!libraryDialogOpen) return;

    setLoadingLibrary(true);
    const q = query(collection(db, 'formLibrary'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const forms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLibraryForms(forms);
        setLoadingLibrary(false);
      },
      (error) => {
        console.error('Kütüphane formları alınırken hata:', error);
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.libraryLoadError'),
          severity: 'error'
        });
        setLoadingLibrary(false);
      }
    );
    return () => unsubscribe();
  }, [libraryDialogOpen, t]);

  // Kütüphaneden formu projeye ekleme
  const handleImportFromLibrary = async (libraryForm) => {
    try {
      // Yeni bir questionForm oluştur
      const newFormRef = await addDoc(collection(db, 'questionForms'), {
        channelId: 'global',
        name: `${libraryForm.name} (Kopya)`,
        createdBy: currentUser.uid,
        organizationId: organization?.id || selectedQuestionForm?.organizationId || null,
        createdAt: serverTimestamp(),
        status: 'draft',
        publicShareId: generatePublicShareId(),
        language: libraryForm.language || 'tr',
        showNavigationArrows: true,
        showProgressBar: true,
        showQuestionNumber: true,
        redirectEnabled: false,
        redirectUrl: ''
      });

      // Soruları ekle
      const questionPromises = libraryForm.questions.map((q, index) =>
        addDoc(collection(db, 'questions'), {
          channelId: 'global',
          questionFormId: newFormRef.id,
          organizationId: organization?.id || selectedQuestionForm?.organizationId || null,
          text: q.text,
          questionType: q.questionType,
          choices: q.choices || [],
          allowOther: q.allowOther || false,
          requireMedia: q.requireMedia || false,
          isRequired: q.isRequired || false,
          ratingMin: q.ratingMin || 1,
          ratingMax: q.ratingMax || 5,
          ratingName: q.ratingName || '',
          aiPrompt: q.aiPrompt || '',
          aiCount: q.aiCount || 1,
          cards: q.cards || [],
          groups: q.groups || [],
          infoPlacement: q.infoPlacement || 'inline',
          sortOrder: index,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp()
        })
      );

      await Promise.all(questionPromises);
      setLibraryDialogOpen(false);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.importedFromLibrarySuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Form import edilirken hata:', error);
      setSnackbar({
        open: true,
        message: t('moderatorFormBuilder.importedFromLibraryError'),
        severity: 'error'
      });
    }
  };

  // Kütüphaneden form silme
  const handleDeleteFromLibrary = (formId) => {
    openConfirmDialog(t('moderatorFormBuilder.deleteLibraryFormConfirm'), async () => {
      try {
        await deleteDoc(doc(db, 'formLibrary', formId));
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.deleteLibraryFormSuccess'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Kütüphaneden form silinirken hata:', error);
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.deleteLibraryFormError'),
          severity: 'error'
        });
      }
    });
  };

  const handleEditAddCard = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedCards: [...prev.updatedCards, { text: '', imageUrl: '' }]
    }))
  }
  
  // Edit Dialog için: Kart sil
  const handleEditRemoveCard = (index) => {
    setEditDialog((prev) => {
      const newCards = [...prev.updatedCards]
      newCards.splice(index, 1)
      return { ...prev, updatedCards: newCards }
    })
  }
  
  // Edit Dialog için: Kart metnini güncelle
  const handleEditCardTextChange = (index, text) => {
    setEditDialog((prev) => {
      const newCards = [...prev.updatedCards]
      newCards[index].text = text
      return { ...prev, updatedCards: newCards }
    })
  }
  
  // Edit Dialog için: Kart fotoğrafı yükle
  const handleEditUploadCardImage = async (index) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const url = await uploadFile(file)
        setEditDialog((prev) => {
          const newCards = [...prev.updatedCards]
          newCards[index].imageUrl = url
          return { ...prev, updatedCards: newCards }
        })
      } catch (err) {
        console.error('Kart fotoğrafı yüklenirken hata:', err)
        setSnackbar({
          open: true,
          message: t('moderatorFormBuilder.uploadCardImageError'),
          severity: 'error'
        })
      }
    }
    input.click()
  }
  
  // Edit Dialog için: Grup ekle
  const handleEditAddGroup = () => {
    setEditDialog((prev) => ({
      ...prev,
      updatedGroups: [...prev.updatedGroups, '']
    }))
  }
  
  // Edit Dialog için: Grup sil
  const handleEditRemoveGroup = (index) => {
    setEditDialog((prev) => {
      const newGroups = [...prev.updatedGroups]
      newGroups.splice(index, 1)
      return { ...prev, updatedGroups: newGroups }
    })
  }
  
  // Edit Dialog için: Grup ismini güncelle
  const handleEditGroupChange = (index, value) => {
    setEditDialog((prev) => {
      const newGroups = [...prev.updatedGroups]
      newGroups[index] = value
      return { ...prev, updatedGroups: newGroups }
    })
  }



  // ------------------------------------------------------
  //                   Render
  // ------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 18% 20%, rgba(252,165,165,0.16), transparent 26%), radial-gradient(circle at 82% 10%, rgba(125,211,252,0.16), transparent 24%), linear-gradient(180deg, #fdf2f8 0%, #eef2ff 55%, #e0f2fe 100%)',
        py: { xs: 2, md: 4 },
        px: { xs: 0, sm: 1, md: 3 }
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          position: 'relative'
        }}
      >
        {!selectedQuestionForm && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(252,165,165,0.55), rgba(125,211,252,0.55)), linear-gradient(180deg, #fdf2f8 0%, #e2e8f0 100%)',
              color: '#0f172a',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: '0 26px 60px rgba(241,182,219,0.35)',
              border: '1px solid rgba(148,163,184,0.35)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#1e293b',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' }
                  }}
                >
                  <CodeIcon sx={{ color: '#1d4ed8' }} />
                  {t('moderatorFormBuilder.pageTitle')}
                </Typography>
                <Typography variant="body1" sx={{ color: '#334155', mt: 1, fontSize: { xs: '0.85rem', sm: '1rem' }}}>
                  {t('moderatorFormBuilder.heroSubtitle')}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  sx={{
                    mt: 2,
                    rowGap: 0.5,
                    columnGap: 0.5,
                    maxWidth: '100%'
                  }}
                >
                  <Chip
                    label={`${t('moderatorFormBuilder.totalLabel')}: ${formStats.total}`}
                    size="small"
                    sx={{
                      bgcolor: '#e2e8f0',
                      color: '#0f172a',
                      fontWeight: 700,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      px: { xs: 0.75, sm: 1 },
                      py: { xs: 0.25, sm: 0.4 },
                      boxShadow: '0 0 0 1px rgba(15,23,42,0.08)',
                      maxWidth: '100%'
                    }}
                  />
                  {['draft', 'published', 'completed'].map((statusKey) => {
                    const meta = getStatusMeta(statusKey)
                    return (
                      <Chip
                        key={statusKey}
                        label={`${meta.label}: ${formStats[statusKey] ?? 0}`}
                        size="small"
                        color={meta.color}
                        sx={{
                          bgcolor: meta.bg,
                          color: meta.textColor,
                          fontWeight: 700,
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          px: { xs: 0.75, sm: 1 },
                          py: { xs: 0.25, sm: 0.4 },
                          boxShadow: '0 0 0 1px rgba(15,23,42,0.08)',
                          maxWidth: '100%'
                        }}
                      />
                    )
                  })}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="flex-end"
                  alignItems="stretch"
                  sx={{ height: '100%' }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => setAddFormDialogOpen(true)}
                    sx={{
                      fontWeight: 800,
                      px: { xs: 1.5, sm: 2.5 },
                      py: { xs: 0.8, sm: 1.2 },
                      fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      borderRadius: 2,
                      background: 'linear-gradient(120deg, #c73737ff, #7463c1ff)',
                      boxShadow: '0 18px 40px rgba(167,243,208,0.35)',
                      '&:hover': {
                        background: 'linear-gradient(120deg, #ba2650ff, #6a1619ff)'
                      }
                    }}
                  >
                    {t('moderatorFormBuilder.addNewForm')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CodeIcon />}
                    onClick={() => setLibraryDialogOpen(true)}
                    sx={{
                      color: '#0f172a',
                      borderColor: 'rgba(15,23,42,0.25)',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderRadius: 2,
                      px: { xs: 1.5, sm: 2.5 },
                      py: { xs: 0.8, sm: 1.2 },
                      fontSize: { xs: '0.8rem', sm: '0.95rem' },
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: '#1d4ed8',
                        backgroundColor: '#eff6ff'
                      }
                    }}
                  >
                    {t('moderatorFormBuilder.openLibrary')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        )}

        {!selectedQuestionForm ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.5, md: 2.5 },
                  borderRadius: 4,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.92) 100%)',
                  boxShadow: '0 22px 60px rgba(15,23,42,0.28)',
                  border: '1px solid rgba(148,163,184,0.35)',
                  backdropFilter: 'blur(10px)'
                }}
              >

                {/* Başlık + Arama & Durum Filtresi */}
                <Box
                  sx={{
                    mb: 1,
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' }
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    flexWrap="wrap"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('moderatorFormBuilder.formListTitle')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      flexGrow: 1
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder={t('moderatorFormBuilder.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: 'rgba(148,163,184,0.5)' },
                          '&:hover fieldset': { borderColor: '#93c5fd' },
                          '&.Mui-focused fieldset': { borderColor: '#93c5fd', borderWidth: 2 }
                        }
                      }}
                    />

                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <InputLabel id="status-filter-label">
                        {t('moderatorFormBuilder.statusFilterLabel') || 'Durum'}
                      </InputLabel>
                      <Select
                        labelId="status-filter-label"
                        label={t('moderatorFormBuilder.statusFilterLabel')}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value="">
                          {t('moderatorFormBuilder.statusAll')}
                        </MenuItem>
                        <MenuItem value="draft">{t('moderatorFormBuilder.statusDraft')}</MenuItem>
                        <MenuItem value="published">{t('moderatorFormBuilder.statusPublished')}</MenuItem>
                        <MenuItem value="completed">{t('moderatorFormBuilder.statusCompleted')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />
                
                <Box
                  sx={{
                    maxHeight: { xs: 360, md: 480 },   // istediğin yüksekliğe göre oynayabilirsin
                    overflowY: 'auto',
                    pr: 0.5
                  }}
                >
                  
                {loadingQuestionForms ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredForms.length > 0 ? (
                  <Grid container spacing={1}>
                    {filteredForms.map((form) => {
                      const meta = getStatusMeta(form.status)
                      return (
                        <Grid item xs={12} sm={12} md={12} key={form.id}>
                          <Paper
                            onClick={() => setSelectedQuestionForm(form)}
                            sx={{
                              p: { xs: 1.5, md: 2 },
                              mb: 1,
                              borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.96)',
                              border: '1px solid rgba(148,163,184,0.4)',
                              cursor: 'pointer',
                              transition: 'all 0.22s ease',
              
                              '&:hover': {
                                boxShadow: '0 10px 40px rgba(15,23,42,0.18)',
                                transform: 'translateY(-2px)',
                                bgcolor: '#fff'
                              }
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="flex-start"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {form.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '11px' }}>
                                  {t('moderatorFormBuilder.createdDate')}:{' '}
                                  {form.createdAt?.toDate
                                    ? form.createdAt.toDate().toLocaleString()
                                    : '—'}
                                </Typography>
                                {form.publicShareId && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.4 }}>
                                    <Tooltip title={t('moderatorFormBuilder.tooltipCopyShareLink')}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleCopyShareLink(form)
                                        }}
                                      >
                                        <LinkIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#334155',
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-all'
                                      }}
                                    >
                                      {`${window.location.origin}/#/forms/${form.publicShareId}`}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <Tooltip title={t('moderatorFormBuilder.tooltipEditForm')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleOpenRenameDialog(form)
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('moderatorFormBuilder.tooltipDeleteForm')}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteQuestionForm(form.id)
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('moderatorFormBuilder.tooltipDuplicateForm')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDuplicateForm(form)
                                      }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('moderatorFormBuilder.tooltipSaveToLibrary')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleSaveToLibrary(form)
                                      }}
                                    >
                                      <LibraryAddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Chip
                                    label={meta.label}
                                    size="small"
                                    color={meta.color}
                                    sx={{
                                      bgcolor: meta.bg,
                                      color: meta.textColor,
                                      fontWeight: 700
                                    }}
                                  />
                                </Box>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1
                              }}
                            >
                              {/* Sol taraf: Yayınla / Tamamla / Taslağa Dön / Tamamlandı etiketi */}
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                                {form.status === 'draft' && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PublishIcon fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handlePublish(form)
                                    }}
                                    sx={{ fontSize: '11px', px: 1, py: 0.3 }}
                                  >
                                    {t('moderatorFormBuilder.formPublishButton')}
                                  </Button>
                                )}
                                {form.status === 'published' && (
                                  <>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<DoneAllIcon fontSize="small" />}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleMarkComplete(form)
                                      }}
                                      sx={{ fontSize: '11px', px: 1, py: 0.3 }}
                                    >
                                      {t('moderatorFormBuilder.formCompletedButton')}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="warning"
                                      size="small"
                                      startIcon={<UndoIcon fontSize="small" />}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleRevertToDraft(form)
                                      }}
                                      sx={{ fontSize: '11px', px: 1, py: 0.3 }}
                                    >
                                      {t('moderatorFormBuilder.formDraftButton')}
                                    </Button>
                                  </>
                                )}
                                {form.status === 'completed' && (
                                  <Chip
                                    label={t('moderatorFormBuilder.formCompletedLabel')}
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 700 }}
                                  />
                                )}
                              </Stack>

                              {/* Sağ taraf: Dashboard / Rapor / Analiz Sayfası */}
                              {form.status !== 'draft' && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<BarChartIcon fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      const params = new URLSearchParams({ formId: form.id })
                                      if (form.channelId && form.channelId !== 'global') {
                                        params.append('channelId', form.channelId)
                                      }
                                      navigate(`/view-answers?${params.toString()}`)
                                    }}
                                    sx={{ fontSize: '11px', px: 1, py: 0.3 }}
                                  >
                                    {t('todoPage.reportDashboard')}
                                  </Button>                                 
                                </Stack>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      )
                    })}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      border: '1px dashed #e2e8f0',
                      borderRadius: 2,
                      bgcolor: '#f8fafc'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t('moderatorFormBuilder.noFormsYet')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      Yeni bir form oluşturarak başlayın veya kütüphaneden içe aktarın.
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{ mt: 2 }}
                      onClick={() => setLibraryDialogOpen(true)}
                    >
                      {t('moderatorFormBuilder.openLibrary')}
                    </Button>
                  </Box>
                )}
              </Box>
              </Paper>
            </Grid>

            
          </Grid>
        ) : (
          <>
            <Paper
              sx={{
                mb: 3,
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(241,245,249,0.95))',
                border: '1px solid rgba(148,163,184,0.35)',
                boxShadow: '0 18px 38px rgba(15,23,42,0.18)'
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setSelectedQuestionForm(null)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700
                    }}
                  >
                    {t('moderatorFormBuilder.goBackForms')}
                  </Button>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selectedQuestionForm.name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handlePreviewForm(selectedQuestionForm)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 800,
                      background: 'linear-gradient(120deg, #50577aff, #8fb1c3ff)',
                      boxShadow: '0 14px 30px rgba(165,180,252,0.3)'
                    }}
                  >
                    {t('moderatorFormBuilder.previewOnUserPage')}
                  </Button>
                  <Chip
                    label={getStatusMeta(selectedQuestionForm.status).label}
                    color={getStatusMeta(selectedQuestionForm.status).color}
                    sx={{
                      bgcolor: getStatusMeta(selectedQuestionForm.status).bg,
                      color: getStatusMeta(selectedQuestionForm.status).textColor,
                      fontWeight: 700
                    }}
                    size="small"
                  />
                  <Chip
                    label={`${questions.length} soru`}
                    size="small"
                    sx={{ bgcolor: '#eef2ff', color: '#1d4ed8', fontWeight: 700 }}
                  />
                </Stack>
              </Box>
            </Paper>



            {selectedQuestionForm.status === 'draft' && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 4,
                  p: { xs: 2.5, md: 3.5 },
                  borderRadius: 4,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.92) 100%)',
                  boxShadow: '0 26px 60px rgba(15, 23, 42, 0.2)',
                  border: '1px solid rgba(148,163,184,0.35)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                      {t('moderatorFormBuilder.newQuestionTitle')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('moderatorFormBuilder.inlineQuillHint')}
                    </Typography>
                  </Box>
                </Box>

                {/* Typeform tarzı ikonik soru türü seçimi */}
                <Box
                  sx={{
                    mb: 2.5,
                    p: 1.5,
                    border: '1px dashed #cbd5e1',
                    borderRadius: 3,
                    backgroundColor: '#f8fafc'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 700, color: '#0f172a', letterSpacing: 0.2 }}
                  >
                    {t('moderatorFormBuilder.questionTypeLabel')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.75, sm: 1.25 },
                      justifyContent: { xs: 'space-between', sm: 'flex-start' }
                    }}
                  >
                    {questionTypeOptions.map((option) => {
                      const palette =
                        option.palette || QUESTION_TYPE_PALETTES[option.value] || QUESTION_TYPE_PALETTES.default
                      const isActive = newQuestionType === option.value

                      return (
                        <Paper
                          key={option.value}
                          onClick={() => setNewQuestionType(option.value)}
                          sx={{
                            px: { xs: 1.1, sm: 1.6 },
                            py: { xs: 0.75, sm: 1.1 },
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.75, sm: 1 },
                            cursor: 'pointer',
                            border: isActive ? `2px solid ${palette.border}` : '1px solid #e2e8f0',
                            bgcolor: isActive ? palette.activeBg : '#ffffff',
                            boxShadow: isActive
                              ? `0 10px 20px ${palette.activeShadow || 'rgba(148, 163, 184, 0.25)'}`
                              : '0 6px 14px rgba(15, 23, 42, 0.06)',
                            transition: 'all 0.2s ease',
                            minWidth: { xs: '48%', sm: 150 },
                            maxWidth: { xs: '48%', sm: 220 },
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: isActive ? palette.activeBg : palette.inactiveBg,
                              color: isActive ? palette.activeColor : palette.inactiveColor,
                              flexShrink: 0,
                              '& svg': {
                                fontSize: { xs: 16, sm: 18 },
                                color: isActive ? palette.activeColor : palette.inactiveColor
                              }
                            }}
                          >
                            {option.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isActive ? 800 : 600,
                              color: isActive ? palette.activeColor : '#0f172a',
                              fontSize: { xs: 11, sm: 13 }
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Paper>
                      )
                    })}
                  </Box>
                </Box>

                {/* Soru metni editörü */}
                <Box
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
                    mb: 2,
                    '& .ql-toolbar.ql-snow': {
                      border: 'none',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc'
                    },
                    '& .ql-container.ql-snow': {
                      border: 'none',
                      minHeight: 200,
                      fontSize: '16px',
                      backgroundColor: '#ffffff'
                    }
                  }}
                >
                  <InlineQuillEditor
                    value={newQuestionText}
                    onChange={handleNewQuestionTextChange}
                  />
                </Box>

                {(newQuestionType === 'singleChoice' || newQuestionType === 'multipleChoice') && (
                  <Box
                    mt={2}
                    sx={{
                      p: 2,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {t('moderatorFormBuilder.optionsLabel')}
                    </Typography>
                    {newChoices.map((choice, index) => (
                      <Box key={index} display="flex" alignItems="center" mt={1}>
                        {newQuestionType === 'singleChoice' ? <Radio disabled /> : <Checkbox disabled />}
                        <TextField
                          value={choice}
                          onChange={(e) => handleChoiceChange(index, e.target.value)}
                          fullWidth
                          size="small"
                          placeholder={`Option ${index + 1}`}
                          sx={{ mr: 1 }}
                        />
                        <IconButton
                          onClick={() => handleRemoveChoice(index)}
                          disabled={newChoices.length === 1 && index === 0}
                          color="error"
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddChoice}
                      sx={{ mt: 1 }}
                    >
                      {t('moderatorFormBuilder.addChoice')}
                    </Button>
                    <FormControlLabel
                      sx={{ display: 'block', mt: 2 }}
                      control={
                        <Switch
                          checked={newAllowOther}
                          onChange={(e) => setNewAllowOther(e.target.checked)}
                          size="medium"
                        />
                      }
                      label={t('moderatorFormBuilder.allowOther')}
                    />
                  </Box>
                )}

                {newQuestionType === 'pictureChoice' && (
                  <Box
                    mt={2}
                    sx={{
                      p: 2,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('moderatorFormBuilder.pictureChoiceSettings')}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newPictureAllowMultiple}
                            onChange={(e) => setNewPictureAllowMultiple(e.target.checked)}
                          />
                        }
                        label={t('moderatorFormBuilder.pictureAllowMultiple')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={newPictureAllowOther}
                            onChange={(e) => setNewPictureAllowOther(e.target.checked)}
                          />
                        }
                        label={t('moderatorFormBuilder.pictureAllowOther')}
                      />
                    </Stack>
                    {newPictureChoices.map((choice, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderColor: '#e2e8f0' }}
                      >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                          <Box sx={{ width: 96, height: 72, border: '1px dashed #cbd5e1', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fff' }}>
                            {choice.imageUrl ? (
                              <img src={choice.imageUrl} alt="choice" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                {t('moderatorFormBuilder.uploadImage')}
                              </Typography>
                            )}
                          </Box>
                          <Button variant="outlined" onClick={() => handlePictureChoiceUpload(index)}>
                            {choice.imageUrl
                              ? t('moderatorFormBuilder.changeImage')
                              : t('moderatorFormBuilder.uploadImage')}
                          </Button>
                          <TextField
                            size="small"
                            label={t('moderatorFormBuilder.choiceLabel')}
                            value={choice.label}
                            onChange={(e) => handlePictureChoiceLabelChange(index, e.target.value)}
                            fullWidth
                          />
                          <IconButton onClick={() => handleRemovePictureChoice(index)} color="error">
                            <ClearIcon />
                          </IconButton>
                        </Stack>
                      </Paper>
                    ))}
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddPictureChoice}>
                      {t('moderatorFormBuilder.addChoice')}
                    </Button>
                  </Box>
                )}

                {newQuestionType === 'screening' && (
                  <Box
                    mt={2}
                    sx={{
                      p: 2,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('moderatorFormBuilder.screeningSettings')}
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="screening-mode-select">
                        {t('moderatorFormBuilder.screeningMode')}
                      </InputLabel>
                      <Select
                        labelId="screening-mode-select"
                        label={t('moderatorFormBuilder.screeningMode')}
                        value={newScreeningMode}
                        onChange={(e) => setNewScreeningMode(e.target.value)}
                        size="small"
                      >
                        <MenuItem value="single">
                          {t('moderatorFormBuilder.screeningModeSingle')}
                        </MenuItem>
                        <MenuItem value="multiple">
                          {t('moderatorFormBuilder.screeningModeMultiple')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {newScreeningChoices.map((choice, index) => (
                      <Box key={index} sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {newScreeningMode === 'single' ? <Radio disabled /> : <Checkbox disabled />}
                          <TextField
                            value={choice.text}
                            onChange={(e) => handleScreeningChoiceChange(index, 'text', e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={`${t('moderatorFormBuilder.choiceLabel')} ${index + 1}`}
                          />
                          <IconButton onClick={() => handleRemoveScreeningChoice(index)} color="error">
                            <ClearIcon />
                          </IconButton>
                        </Box>
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Checkbox
                              checked={choice.screenOut}
                              onChange={(e) =>
                                handleScreeningChoiceChange(index, 'screenOut', e.target.checked)
                              }
                            />
                          }
                          label={t('moderatorFormBuilder.markScreenout')}
                        />
                        {choice.screenOut && (
                          <TextField
                            fullWidth
                            size="small"
                            sx={{ mt: 1 }}
                            value={choice.screenOutText}
                            onChange={(e) =>
                              handleScreeningChoiceChange(index, 'screenOutText', e.target.value)
                            }
                            placeholder={t('moderatorFormBuilder.screenoutMessagePlaceholder')}
                          />
                        )}
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddScreeningChoice}
                    >
                      {t('moderatorFormBuilder.addChoice')}
                    </Button>
                  </Box>
                )}

                {newQuestionType === 'ratingQuestion' && (
                  <Box mt={2}>
                    <TextField
                      label={t('moderatorFormBuilder.ratingScaleName')}
                      fullWidth
                      value={newRatingName}
                      onChange={(e) => setNewRatingName(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Box display="flex" gap={2}>
                      <TextField
                        type="number"
                        label={t('moderatorFormBuilder.ratingMin')}
                        value={newRatingMin}
                        onChange={(e) => setNewRatingMin(Number(e.target.value))}
                      />
                      <TextField
                        type="number"
                        label={t('moderatorFormBuilder.ratingMax')}
                        value={newRatingMax}
                        onChange={(e) => setNewRatingMax(Number(e.target.value))}
                      />
                    </Box>
                  </Box>
                )}

                {newQuestionType === 'masked' && (
                  <Box
                    mt={2}
                    sx={{
                      p: 2,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t('moderatorFormBuilder.maskedSettings')}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel id="masked-type-select">
                        {t('moderatorFormBuilder.maskedType')}
                      </InputLabel>
                      <Select
                        labelId="masked-type-select"
                        label={t('moderatorFormBuilder.maskedType')}
                        value={newMaskedType}
                        onChange={(e) => setNewMaskedType(e.target.value)}
                      >
                        <MenuItem value="number">{t('moderatorFormBuilder.maskedNumber')}</MenuItem>
                        <MenuItem value="date">{t('moderatorFormBuilder.maskedDate')}</MenuItem>
                        <MenuItem value="email">{t('moderatorFormBuilder.maskedEmail')}</MenuItem>
                        <MenuItem value="yesno">{t('moderatorFormBuilder.maskedYesNo')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {newQuestionType === 'aiModerator' && (
                  <Box mt={2}>
                    <TextField
                      label={t('moderatorFormBuilder.aiPromptLabel')}
                      value={newAiPrompt}
                      onChange={(e) => setNewAiPrompt(e.target.value)}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      type="number"
                      label={t('moderatorFormBuilder.aiCountLabel')}
                      value={newAiCount}
                      onChange={(e) => setNewAiCount(Number(e.target.value))}
                      inputProps={{ min: 1, max: 5 }}
                      fullWidth
                    />
                  </Box>
                )}

                {newQuestionType === 'cardSorting' && (
                  <>
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {t('moderatorFormBuilder.cards')}
                      </Typography>
                      {newCards.map((card, index) => (
                        <Box key={index} display="flex" alignItems="center" mt={1}>
                          <TextField
                            value={card.text}
                            onChange={(e) => handleCardTextChange(index, e.target.value)}
                            placeholder={`Kart ${index + 1}`}
                            sx={{ mr: 1 }}
                          />
                          <Button onClick={() => handleUploadCardImage(index)}>                       
                            {card.imageUrl ? t('moderatorFormBuilder.changeImage') : t('moderatorFormBuilder.uploadImage')}
                          </Button>
                          {card.imageUrl && (
                            <img
                              src={card.imageUrl}
                              alt="Önizleme"
                              style={{ width: 50, height: 50, marginRight: 8 }}
                            />
                          )}
                          <IconButton onClick={() => handleRemoveCard(index)}>
                            <ClearIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button variant="outlined" onClick={handleAddCard} sx={{ mt: 1 }}>
                        {t('moderatorFormBuilder.addCard')}
                      </Button>
                    </Box>

                    <Box
                      mt={2}
                      sx={{
                        p: 2,
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {t('moderatorFormBuilder.groups')}
                      </Typography>
                      {newGroups.map((group, index) => (
                        <Box key={index} display="flex" alignItems="center" mt={1}>
                          <TextField
                            value={group}
                            onChange={(e) => handleGroupChange(index, e.target.value)}
                            placeholder={`Grup ${index + 1}`}
                            sx={{ mr: 1 }}
                          />
                          <IconButton onClick={() => handleRemoveGroup(index)}>
                            <ClearIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button variant="outlined" onClick={handleAddGroup} sx={{ mt: 1 }}>
                       {t('moderatorFormBuilder.addGroup')}
                      </Button>
                    </Box>
                  </>
                )}

                {newQuestionType === 'infoQuestion' && (
                  <Box
                    mt={2}
                    sx={{
                      p: 2,
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {t(
                        'moderatorFormBuilder.infoPlacementLabel'
                      )}
                    </Typography>
                    <RadioGroup
                      row
                      value={newInfoPlacement}
                      onChange={(e) => setNewInfoPlacement(e.target.value)}
                    >
                      <FormControlLabel
                        value="start"
                        control={<Radio />}
                        label={t(
                          'moderatorFormBuilder.infoPlacementStart'
                        )}
                      />
                      <FormControlLabel
                        value="inline"
                        control={<Radio />}
                        label={t(
                          'moderatorFormBuilder.infoPlacementInline'
                        )}
                      />
                      <FormControlLabel
                        value="end"
                        control={<Radio />}
                        label={t(
                          'moderatorFormBuilder.infoPlacementEnd'
                        )}
                      />
                    </RadioGroup>
                  </Box>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mt={2}>
                  {newQuestionType === 'openEnded' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newRequireMedia}
                          onChange={(e) => setNewRequireMedia(e.target.checked)}
                          size="medium"
                        />
                      }
                      label={t('moderatorFormBuilder.requireMediaLabel')}
                      sx={{ m: 0 }}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newRequired}
                        onChange={(e) => setNewRequired(e.target.checked)}
                        size="medium"
                      />
                    }
                    label={t('moderatorFormBuilder.questionRequiredLabel')}
                    sx={{ m: 0 }}
                  />
                </Stack>
                <Box mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuestion}
                    fullWidth
                    sx={{
                      py: 1.4,
                      fontWeight: 800,
                      textTransform: 'none',
                      borderRadius: 2.5,
                      background: 'linear-gradient(120deg, #a5b4fc, #bae6fd)',
                      boxShadow: '0 16px 34px rgba(165,180,252,0.35)',
                      '&:hover': {
                        background: 'linear-gradient(120deg, #93a5f5, #a6dbf5)'
                      }
                    }}
                  >
                    {t('moderatorFormBuilder.add')}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Sorular Listesi (Drag&Drop) */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.92) 100%)',
                boxShadow: '0 22px 60px rgba(15, 23, 42, 0.2)',
                border: '1px solid rgba(148,163,184,0.35)',
                backdropFilter: 'blur(6px)'
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                gap={2}
                flexWrap="wrap"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <ForumIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {t('moderatorFormBuilder.existingQuestions')}
                  </Typography>
                </Stack>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {loadingQuestions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="questionsDroppable">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {questions.length > 0 ? (
                          questions.map((question, index) => (
                            <Draggable
                              key={question.id}
                              draggableId={question.id}
                              index={index}
                              isDragDisabled={selectedQuestionForm?.status !== 'draft'}
                            >
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  sx={{
                                    mb: 1.5,
                                    px: 1.75,
                                    py: 1.25,
                                    borderRadius: 2,
                                    background: snapshot.isDragging
                                      ? 'linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%)'
                                      : '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: snapshot.isDragging
                                      ? '0 12px 24px rgba(148, 163, 184, 0.25)'
                                      : 'none',
                                    minHeight: 64,
                                    ...provided.draggableProps.style
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1.5}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1.5}
                                      flexGrow={1}
                                      minWidth={0}
                                    >
                                      <Box
                                        sx={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: 1.25,
                                          backgroundColor: '#e2e8f0',
                                          color: '#0f172a',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 700,
                                          fontSize: 14,
                                          flexShrink: 0
                                        }}
                                      >
                                        {index + 1}
                                      </Box>
                                      <Chip
                                        label={getQuestionTypeLabel(question.questionType)}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#e0e7ff',
                                          color: '#312e81',
                                          fontWeight: 700,
                                          textTransform: 'none',
                                          height: 28,
                                          flexShrink: 0
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          flexGrow: 1,
                                          minWidth: 0,
                                          fontWeight: 600,
                                          color: '#0f172a',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          lineHeight: 1.4
                                        }}
                                      >
                                        {getQuestionPreview(question.text) ||
                                          t('moderatorFormBuilder.questionTextEmptyWarning')}
                                      </Typography>
                                      {question.isRequired && (
                                        <Typography
                                          variant="caption"
                                          color="error"
                                          sx={{ fontWeight: 700, flexShrink: 0 }}
                                        >
                                          *
                                        </Typography>
                                      )}
                                    </Stack>
                                    <Stack direction="row" spacing={0.75}>
                                      <IconButton
                                        color="primary"
                                        disabled={selectedQuestionForm?.status !== 'draft'}
                                        onClick={() => handleEditQuestion(question)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        color="secondary"
                                        disabled={selectedQuestionForm?.status !== 'draft'}
                                        onClick={() => handleDuplicateQuestion(question)}
                                      >
                                        <ContentCopyIcon />
                                      </IconButton>
                                      <IconButton
                                        color="error"
                                        disabled={selectedQuestionForm?.status !== 'draft'}
                                        onClick={() => handleDeleteQuestion(question.id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Stack>
                                  </Box>
                                </Box>
                              )}
                            </Draggable>
                          ))
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {t('moderatorFormBuilder.noQuestionsYet')}
                          </Typography>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Paper>
          </>
        )}

        {/* Yeni Form Adı Dialog */}
        <Dialog
          open={addFormDialogOpen}
          onClose={() => setAddFormDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: glassDialogPaperSx }}
        >
          <DialogTitle sx={dialogTitleBarSx}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {t('moderatorFormBuilder.newFormName')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('moderatorFormBuilder.newFormSubtitle')}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              gap: 2,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(241,245,249,0.85))'
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.25)',
                backgroundColor: 'rgba(255,255,255,0.95)',
                boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                {t('moderatorFormBuilder.formInfoSection')}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label={t('moderatorFormBuilder.newFormName')}
                value={newFormName}
                onChange={(e) => setNewFormName(e.target.value)}
                fullWidth
                sx={{ mt: 1 }}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel id="new-form-language-label">
                  {t('moderatorFormBuilder.formLanguageLabel')}
                </InputLabel>
                <Select
                  labelId="new-form-language-label"
                  label={t('moderatorFormBuilder.formLanguageLabel')}
                  value={newFormLanguage}
                  onChange={(e) => setNewFormLanguage(e.target.value)}
                >
                  {languageOptions.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.25)',
                backgroundColor: 'rgba(255,255,255,0.95)',
                boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                {t('moderatorFormBuilder.redirectSectionTitle')}
              </Typography>
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Switch
                    checked={newFormRedirectEnabled}
                    onChange={(e) => setNewFormRedirectEnabled(e.target.checked)}
                  />
                }
                label={t(
                  'moderatorFormBuilder.redirectEnabled'
                )}
              />
              {newFormRedirectEnabled && (
                <TextField
                  margin="dense"
                  label={t('moderatorFormBuilder.redirectUrl')}
                  placeholder="https://www.evoosurvey.com"
                  fullWidth
                  value={newFormRedirectUrl}
                  error={
                    newFormRedirectUrl &&
                    !newFormRedirectUrl.startsWith('https://')
                  }
                  helperText={
                    newFormRedirectUrl &&
                    !newFormRedirectUrl.startsWith('https://')
                      ? t('moderatorFormBuilder.redirectUrlMustBeHttps', 'URL must start with https://')
                      : ''
                  }
                  onChange={(e) => setNewFormRedirectUrl(e.target.value)}
                />
              )}
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.25)',
                backgroundColor: 'rgba(255,255,255,0.95)',
                boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5, mb: 1 }}>
                {t('moderatorFormBuilder.displaySettings')}
              </Typography>
              <Stack spacing={1.2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newFormShowNavigationArrows}
                      onChange={(e) => setNewFormShowNavigationArrows(e.target.checked)}
                    />
                  }
                  label={t(
                    'moderatorFormBuilder.showNavigationArrows'
                  )}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newFormShowProgressBar}
                      onChange={(e) => setNewFormShowProgressBar(e.target.checked)}
                    />
                  }
                  label={t('moderatorFormBuilder.showProgressBar')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newFormShowQuestionNumber}
                      onChange={(e) => setNewFormShowQuestionNumber(e.target.checked)}
                    />
                  }
                  label={t('moderatorFormBuilder.showQuestionNumber')}
                />
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions sx={dialogActionsBarSx}>
            <Button onClick={() => setAddFormDialogOpen(false)} color="secondary" variant="text">
              {t('moderatorFormBuilder.cancel')}
            </Button>
            <Button variant="contained" onClick={handleCreateQuestionForm} sx={{ px: 2.5 }}>
              {t('moderatorFormBuilder.add')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Düzenleme Dialog */}
        <Dialog
        open={editDialog.open}
        onClose={() =>
          setEditDialog({
            open: false,
            question: null,
            updatedText: '',
            updatedType: 'openEnded',
            updatedChoices: [],
            updatedAllowOther: false,
            updatedRequireMedia: false,
            updatedRequired: false,
            updatedRatingMin: 1,
            updatedRatingMax: 5,
            updatedRatingName: '',
            updatedAiPrompt: '',
            updatedAiCount: 1,
            updatedScreeningMode: 'single',
            updatedScreeningChoices: [],
            updatedPictureChoices: [],
            updatedPictureAllowMultiple: false,
            updatedPictureAllowOther: false,
            updatedMaskedType: 'number',
            updatedInfoPlacement: 'inline'
          })
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('moderatorFormBuilder.editQuestionDialogTitle')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            {t('moderatorFormBuilder.editQuillHint')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              m: { xs: 2, md: 3 },
              p: { xs: 2.5, md: 3 },
              borderRadius: 4,
              background: 'linear-gradient(180deg, #f5f7ff 0%, #ffffff 55%)',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Soru türü seçimi - Typeform tarzı */}
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                border: '1px dashed #cbd5e1',
                borderRadius: 3,
                backgroundColor: '#f8fafc'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1.5, fontWeight: 700, color: '#0f172a', letterSpacing: 0.2 }}
              >
                {t('moderatorFormBuilder.questionTypeLabel')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 0.75, sm: 1.25 },
                  justifyContent: { xs: 'space-between', sm: 'flex-start' }
                }}
              >
                {questionTypeOptions.map((option) => {
                  const palette =
                    option.palette || QUESTION_TYPE_PALETTES[option.value] || QUESTION_TYPE_PALETTES.default
                  const isActive = editDialog.updatedType === option.value

                  return (
                    <Paper
                      key={option.value}
                      onClick={() =>
                        setEditDialog((prev) => ({ ...prev, updatedType: option.value }))
                      }
                      sx={{
                        px: { xs: 1.1, sm: 1.6 },
                        py: { xs: 0.75, sm: 1.1 },
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.75, sm: 1 },
                        cursor: 'pointer',
                        border: isActive ? `2px solid ${palette.border}` : '1px solid #e2e8f0',
                        bgcolor: isActive ? palette.activeBg : '#ffffff',
                        boxShadow: isActive
                          ? `0 10px 20px ${palette.activeShadow || 'rgba(148, 163, 184, 0.25)'}`
                          : '0 6px 14px rgba(15, 23, 42, 0.06)',
                        transition: 'all 0.2s ease',
                        minWidth: { xs: '48%', sm: 150 },
                        maxWidth: { xs: '48%', sm: 220 },
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isActive ? palette.activeBg : palette.inactiveBg,
                          color: isActive ? palette.activeColor : palette.inactiveColor,
                          flexShrink: 0,
                          '& svg': {
                            fontSize: { xs: 16, sm: 18 },
                            color: isActive ? palette.activeColor : palette.inactiveColor
                          }
                        }}
                      >
                        {option.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 800 : 600,
                          color: isActive ? palette.activeColor : '#0f172a',
                          fontSize: { xs: 11, sm: 13 }
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Paper>
                  )
                })}
              </Box>
            </Box>

            {/* Soru metni editörü */}
            <Box
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2.5,
                overflow: 'hidden',
                boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
                mb: 2,
                '& .ql-toolbar.ql-snow': {
                  border: 'none',
                  borderBottom: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                },
                '& .ql-container.ql-snow': {
                  border: 'none',
                  minHeight: 200,
                  fontSize: '16px',
                  backgroundColor: '#ffffff'
                }
              }}
            >
              <InlineQuillEditor
                value={editDialog.updatedText}
                onChange={handleEditQuestionTextChange}
              />
            </Box>

            {/* Tek/Çok seçimli seçenekler */}
            {(editDialog.updatedType === 'singleChoice' ||
              editDialog.updatedType === 'multipleChoice') && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {t('moderatorFormBuilder.optionsLabel')}
                </Typography>
                {editDialog.updatedChoices.map((choice, index) => (
                  <Box key={index} display="flex" alignItems="center" mt={1}>
                    {editDialog.updatedType === 'singleChoice' ? (
                      <Radio disabled size="small" />
                    ) : (
                      <Checkbox disabled size="small" />
                    )}
                    <TextField
                      value={choice}
                      onChange={(e) => handleEditChoiceChange(index, e.target.value)}
                      fullWidth
                      size="small"
                      placeholder={`Option ${index + 1}`}
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleEditRemoveChoice(index)}
                      disabled={editDialog.updatedChoices.length === 1 && index === 0}
                      color="error"
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleEditAddChoice} sx={{ mt: 1 }}>
                  {t('moderatorFormBuilder.addChoice')}
                </Button>
                <FormControlLabel
                  sx={{ display: 'block', mt: 2 }}
                  control={
                    <Switch
                      checked={editDialog.updatedAllowOther}
                      onChange={(e) =>
                        setEditDialog((prev) => ({ ...prev, updatedAllowOther: e.target.checked }))
                      }
                      size="medium"
                    />
                  }
                  label={t('moderatorFormBuilder.allowOther')}
                />
              </Box>
            )}

            {editDialog.updatedType === 'pictureChoice' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('moderatorFormBuilder.pictureChoiceSettings')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editDialog.updatedPictureAllowMultiple}
                        onChange={(e) =>
                          setEditDialog((prev) => ({
                            ...prev,
                            updatedPictureAllowMultiple: e.target.checked
                          }))
                        }
                      />
                    }
                    label={t('moderatorFormBuilder.pictureAllowMultiple')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editDialog.updatedPictureAllowOther}
                        onChange={(e) =>
                          setEditDialog((prev) => ({
                            ...prev,
                            updatedPictureAllowOther: e.target.checked
                          }))
                        }
                      />
                    }
                    label={t('moderatorFormBuilder.pictureAllowOther')}
                  />
                </Stack>
                {(editDialog.updatedPictureChoices || []).map((choice, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderColor: '#e2e8f0' }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                      <Box sx={{ width: 96, height: 72, border: '1px dashed #cbd5e1', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fff' }}>
                        {choice.imageUrl ? (
                          <img src={choice.imageUrl} alt="choice" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {t('moderatorFormBuilder.uploadImage')}
                          </Typography>
                        )}
                      </Box>
                      <Button variant="outlined" onClick={() => handleEditPictureChoiceUpload(index)}>
                        {choice.imageUrl
                          ? t('moderatorFormBuilder.changeImage')
                          : t('moderatorFormBuilder.uploadImage')}
                      </Button>
                      <TextField
                        size="small"
                        label={t('moderatorFormBuilder.choiceLabel')}
                        value={choice.label}
                        onChange={(e) => handleEditPictureChoiceLabelChange(index, e.target.value)}
                        fullWidth
                      />
                      <IconButton onClick={() => handleEditRemovePictureChoice(index)} color="error">
                        <ClearIcon />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleEditAddPictureChoice}
                >
                  {t('moderatorFormBuilder.addChoice')}
                </Button>
              </Box>
            )}

            {editDialog.updatedType === 'screening' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('moderatorFormBuilder.screeningSettings')}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="edit-screening-mode-select">
                    {t('moderatorFormBuilder.screeningMode')}
                  </InputLabel>
                  <Select
                    labelId="edit-screening-mode-select"
                    label={t('moderatorFormBuilder.screeningMode')}
                    value={editDialog.updatedScreeningMode}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, updatedScreeningMode: e.target.value }))
                    }
                    size="small"
                  >
                    <MenuItem value="single">
                      {t('moderatorFormBuilder.screeningModeSingle')}
                    </MenuItem>
                    <MenuItem value="multiple">
                      {t('moderatorFormBuilder.screeningModeMultiple')}
                    </MenuItem>
                  </Select>
                </FormControl>
                {(editDialog.updatedScreeningChoices || []).map((choice, index) => (
                  <Box key={index} sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {editDialog.updatedScreeningMode === 'single' ? <Radio disabled /> : <Checkbox disabled />}
                      <TextField
                        value={choice.text}
                        onChange={(e) => handleEditScreeningChoiceChange(index, 'text', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={`${t('moderatorFormBuilder.choiceLabel')} ${index + 1}`}
                      />
                      <IconButton onClick={() => handleEditRemoveScreeningChoice(index)} color="error">
                        <ClearIcon />
                      </IconButton>
                    </Box>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Checkbox
                          checked={choice.screenOut}
                          onChange={(e) =>
                            handleEditScreeningChoiceChange(index, 'screenOut', e.target.checked)
                          }
                        />
                      }
                      label={t('moderatorFormBuilder.markScreenout')}
                    />
                    {choice.screenOut && (
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        value={choice.screenOutText}
                        onChange={(e) =>
                          handleEditScreeningChoiceChange(index, 'screenOutText', e.target.value)
                        }
                        placeholder={t('moderatorFormBuilder.screenoutMessagePlaceholder')}
                      />
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleEditAddScreeningChoice}
                >
                  {t('moderatorFormBuilder.addChoice')}
                </Button>
              </Box>
            )}

            {/* Rating Question */}
            {editDialog.updatedType === 'ratingQuestion' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <TextField
                  label={t('moderatorFormBuilder.ratingScaleName')}
                  fullWidth
                  value={editDialog.updatedRatingName}
                  onChange={(e) =>
                    setEditDialog((prev) => ({ ...prev, updatedRatingName: e.target.value }))
                  }
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={2}>
                  <TextField
                    type="number"
                    label={t('moderatorFormBuilder.ratingMin')}
                    value={editDialog.updatedRatingMin}
                    onChange={(e) =>
                      setEditDialog((prev) => ({
                        ...prev,
                        updatedRatingMin: Number(e.target.value)
                      }))
                    }
                  />
                  <TextField
                    type="number"
                    label={t('moderatorFormBuilder.ratingMax')}
                    value={editDialog.updatedRatingMax}
                    onChange={(e) =>
                      setEditDialog((prev) => ({
                        ...prev,
                        updatedRatingMax: Number(e.target.value)
                      }))
                    }
                  />
                </Box>
              </Box>
            )}

            {/* AI-Moderator */}
            {editDialog.updatedType === 'aiModerator' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <TextField
                  label={t('moderatorFormBuilder.aiPromptLabel')}
                  fullWidth
                  value={editDialog.updatedAiPrompt}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      updatedAiPrompt: e.target.value
                    }))
                  }
                  sx={{ mb: 2 }}
                />
                <TextField
                  type="number"
                  label={t('moderatorFormBuilder.aiCountLabel')}
                  fullWidth
                  value={editDialog.updatedAiCount}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      updatedAiCount: Number(e.target.value)
                    }))
                  }
                  inputProps={{
                    min: 1,
                    max: 5
                  }}
                />
              </Box>
            )}

            {editDialog.updatedType === 'masked' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('moderatorFormBuilder.maskedSettings')}
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-masked-type-select">
                    {t('moderatorFormBuilder.maskedType')}
                  </InputLabel>
                  <Select
                    labelId="edit-masked-type-select"
                    label={t('moderatorFormBuilder.maskedType')}
                    value={editDialog.updatedMaskedType}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, updatedMaskedType: e.target.value }))
                    }
                  >
                    <MenuItem value="number">{t('moderatorFormBuilder.maskedNumber')}</MenuItem>
                    <MenuItem value="date">{t('moderatorFormBuilder.maskedDate')}</MenuItem>
                    <MenuItem value="email">{t('moderatorFormBuilder.maskedEmail')}</MenuItem>
                    <MenuItem value="yesno">{t('moderatorFormBuilder.maskedYesNo')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {editDialog.updatedType === 'cardSorting' && (
              <>
                {/* Kartlar Bölümü */}
                <Box
                  mt={2}
                  sx={{
                    p: 2,
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {t('moderatorFormBuilder.cards')}
                  </Typography>
                  {editDialog.updatedCards.map((card, index) => (
                    <Box key={index} display="flex" alignItems="center" mt={1}>
                      <TextField
                        value={card.text}
                        onChange={(e) => handleEditCardTextChange(index, e.target.value)}
                        placeholder={`Kart ${index + 1}`}
                        sx={{ mr: 1 }}
                      />
                      <Button onClick={() => handleEditUploadCardImage(index)}>
                        {card.imageUrl ? `${t('moderatorFormBuilder.changeImage')}` : `${t('moderatorFormBuilder.uploadImage')}`}
                      </Button>
                      {card.imageUrl && (
                        <img
                          src={card.imageUrl}
                          alt="Önizleme"
                          style={{ width: 50, height: 50, marginRight: 8 }}
                        />
                      )}
                      <IconButton onClick={() => handleEditRemoveCard(index)}>
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button variant="outlined" onClick={handleEditAddCard} sx={{ mt: 1 }}>
                    {t('moderatorFormBuilder.addCard')}
                  </Button>
                </Box>

                {/* Gruplar Bölümü */}
                <Box
                  mt={2}
                  sx={{
                    p: 2,
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {t('moderatorFormBuilder.groups')}
                  </Typography>
                  {editDialog.updatedGroups.map((group, index) => (
                    <Box key={index} display="flex" alignItems="center" mt={1}>
                      <TextField
                        value={group}
                        onChange={(e) => handleEditGroupChange(index, e.target.value)}
                        placeholder={`Grup ${index + 1}`}
                        sx={{ mr: 1 }}
                      />
                      <IconButton onClick={() => handleEditRemoveGroup(index)}>
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button variant="outlined" onClick={handleEditAddGroup} sx={{ mt: 1 }}>
                    {t('moderatorFormBuilder.addGroup')}
                  </Button>
                </Box>
              </>
            )}

            {editDialog.updatedType === 'infoQuestion' && (
              <Box
                mt={2}
                sx={{
                  p: 2,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  backgroundColor: '#f8fafc'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {t('moderatorFormBuilder.infoPlacementLabel')}
                </Typography>
                <RadioGroup
                  row
                  value={editDialog.updatedInfoPlacement}
                  onChange={(e) =>
                    setEditDialog((prev) => ({
                      ...prev,
                      updatedInfoPlacement: e.target.value
                    }))
                  }
                >
                  <FormControlLabel
                    value="start"
                    control={<Radio />}
                    label={t('moderatorFormBuilder.infoPlacementStart')}
                  />
                  <FormControlLabel
                    value="inline"
                    control={<Radio />}
                    label={t('moderatorFormBuilder.infoPlacementInline')}
                  />
                  <FormControlLabel
                    value="end"
                    control={<Radio />}
                    label={t('moderatorFormBuilder.infoPlacementEnd')}
                  />
                </RadioGroup>
              </Box>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              mt={2}
            >
              {editDialog.updatedType === 'openEnded' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editDialog.updatedRequireMedia}
                      onChange={(e) =>
                        setEditDialog((prev) => ({
                          ...prev,
                          updatedRequireMedia: e.target.checked
                        }))
                      }
                      size="medium"
                    />
                  }
                  label={t('moderatorFormBuilder.requireMediaLabel')}
                  sx={{ m: 0 }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={editDialog.updatedRequired}
                    onChange={(e) =>
                      setEditDialog((prev) => ({ ...prev, updatedRequired: e.target.checked }))
                    }
                    size="medium"
                  />
                }
                label={t('moderatorFormBuilder.questionRequiredLabel')}
                sx={{ m: 0 }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() =>
              setEditDialog({
                open: false,
                question: null,
                updatedText: '',
                updatedType: 'openEnded',
                updatedChoices: [],
                updatedAllowOther: false,
                updatedRequireMedia: false,
                updatedRequired: false,
                updatedRatingMin: 1,
                updatedRatingMax: 5,
                updatedRatingName: '',
                updatedAiPrompt: '',
                updatedAiCount: 1,
                updatedScreeningMode: 'single',
                updatedScreeningChoices: [],
                updatedPictureChoices: [],
                updatedPictureAllowMultiple: false,
                updatedPictureAllowOther: false,
                updatedMaskedType: 'number',
                updatedInfoPlacement: 'inline'
              })
            }
            color="secondary"
            variant="outlined"
          >
            {t('moderatorFormBuilder.cancel')}
          </Button>
          <Button
            onClick={handleSaveEdit}
            color="primary"
            variant="contained"
            sx={{
              py: 1.2,
              px: 3,
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 12px 24px rgba(37, 99, 235, 0.25)'
            }}
          >
            {t('moderatorFormBuilder.save')}
          </Button>
        </DialogActions>
        </Dialog>

        <Dialog
        open={renameDialog.open}
        onClose={handleCancelRename}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: glassDialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleBarSx}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {t('moderatorFormBuilder.renameFormTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('moderatorFormBuilder.newFormSubtitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 3,
            py: 2.5,
            display: 'grid',
            gap: 2,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(241,245,249,0.85))'
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.25)',
              backgroundColor: 'rgba(255,255,255,0.95)',
              boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
              {t('moderatorFormBuilder.formInfoSection')}
            </Typography>
            <TextField
              label={t('moderatorFormBuilder.newFormName')}
              value={renameDialog.newName}
              onChange={(e) =>
                setRenameDialog((prev) => ({ ...prev, newName: e.target.value }))
              }
              fullWidth
              sx={{ mt: 1 }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="rename-form-language-label">
                {t('moderatorFormBuilder.formLanguageLabel')}
              </InputLabel>
              <Select
                labelId="rename-form-language-label"
                label={t('moderatorFormBuilder.formLanguageLabel')}
                value={renameDialog.newLanguage}
                onChange={(e) =>
                  setRenameDialog((prev) => ({ ...prev, newLanguage: e.target.value }))
                }
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.25)',
              backgroundColor: 'rgba(255,255,255,0.95)',
              boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
              {t('moderatorFormBuilder.redirectSectionTitle')}
            </Typography>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  checked={renameDialog.newRedirectEnabled}
                  onChange={(e) =>
                    setRenameDialog((prev) => ({
                      ...prev,
                      newRedirectEnabled: e.target.checked
                    }))
                  }
                />
              }
              label={t('moderatorFormBuilder.redirectEnabled')}
            />
            {renameDialog.newRedirectEnabled && (
              <TextField
                margin="dense"
                label={t('moderatorFormBuilder.redirectUrl')}
                placeholder="https://www.evoosurvey.com"
                fullWidth
                value={renameDialog.newRedirectUrl}
                error={
                  renameDialog.newRedirectUrl &&
                  !renameDialog.newRedirectUrl.startsWith('https://')
                }
                helperText={
                  renameDialog.newRedirectUrl &&
                  !renameDialog.newRedirectUrl.startsWith('https://')
                    ? t('moderatorFormBuilder.redirectUrlMustBeHttps', 'URL must start with https://')
                    : ''
                }
                onChange={(e) =>
                  setRenameDialog((prev) => ({ ...prev, newRedirectUrl: e.target.value }))
                }
              />
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.25)',
              backgroundColor: 'rgba(255,255,255,0.95)',
              boxShadow: '0 12px 30px rgba(15,23,42,0.08)'
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5, mb: 1 }}>
              {t('moderatorFormBuilder.displaySettings')}
            </Typography>
            <Stack spacing={1.2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={renameDialog.newShowNavigationArrows}
                    onChange={(e) =>
                      setRenameDialog((prev) => ({
                        ...prev,
                        newShowNavigationArrows: e.target.checked
                      }))
                    }
                  />
                }
                label={t('moderatorFormBuilder.showNavigationArrows')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={renameDialog.newShowProgressBar}
                    onChange={(e) =>
                      setRenameDialog((prev) => ({
                        ...prev,
                        newShowProgressBar: e.target.checked
                      }))
                    }
                  />
                }
                label={t('moderatorFormBuilder.showProgressBar')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={renameDialog.newShowQuestionNumber}
                    onChange={(e) =>
                      setRenameDialog((prev) => ({
                        ...prev,
                        newShowQuestionNumber: e.target.checked
                      }))
                    }
                  />
                }
                label={t('moderatorFormBuilder.showQuestionNumber', 'Soru numarasını göster')}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsBarSx}>
          <Button onClick={handleCancelRename} color="secondary">
            {t('moderatorFormBuilder.cancel')}
          </Button>
          <Button onClick={handleSaveRename} color="primary" variant="contained" sx={{ px: 2.5 }}>
            {t('moderatorFormBuilder.save')}
          </Button>
        </DialogActions>
        </Dialog>

        <Dialog
        open={libraryDialogOpen}
        onClose={() => setLibraryDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('moderatorFormBuilder.formLibraryTitle')}</DialogTitle>
        <DialogContent>
          {loadingLibrary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : libraryForms.length > 0 ? (
            <List>
              {libraryForms.map((form) => (
                <ListItem
                  key={form.id}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleDeleteFromLibrary(form.id)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleImportFromLibrary(form)}
                      >
                        {t('moderatorFormBuilder.import')}
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={form.name}
                    secondary={`${form.questions.length} ${t('moderatorFormBuilder.questions')}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>{t('moderatorFormBuilder.noLibraryForms')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLibraryDialogOpen(false)}>
            {t('moderatorFormBuilder.close')}
          </Button>
        </DialogActions>
        </Dialog>

        <Dialog
          open={librarySaveDialog.open}
          onClose={handleCloseLibrarySaveDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: glassDialogPaperSx }}
        >
          <DialogTitle sx={dialogTitleBarSx}>
            {t('moderatorFormBuilder.librarySaveDialogTitle')}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              autoFocus
              label={t('moderatorFormBuilder.librarySaveNameLabel')}
              value={librarySaveDialog.name}
              onChange={(e) =>
                setLibrarySaveDialog((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </DialogContent>
          <DialogActions sx={dialogActionsBarSx}>
            <Button onClick={handleCloseLibrarySaveDialog} color="secondary">
              {t('moderatorFormBuilder.cancel')}
            </Button>
            <Button onClick={handleConfirmLibrarySave} variant="contained" color="primary">
              {t('moderatorFormBuilder.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          PaperProps={{ sx: glassDialogPaperSx }}
        >
          <DialogTitle sx={dialogTitleBarSx}>
            {t('moderatorFormBuilder.confirmDialogTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions sx={dialogActionsBarSx}>
            <Button onClick={handleCloseConfirmDialog} color="secondary">
              {t('moderatorFormBuilder.cancel')}
            </Button>
            <Button onClick={handleConfirmDialogConfirm} variant="contained" color="primary">
              {t('moderatorFormBuilder.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  )
}
